import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { 
  Target, Users, Package, FileText, DollarSign, Calendar, 
  ChevronRight, ChevronLeft, ChevronDown, Workflow, 
  Megaphone, Settings, Download, Edit2, Plus, Trash2,
  Layers, UserCheck, FileImage,
  Sparkles, TrendingUp, Check, X,
  Save, Play, Zap, Circle,
  FileStack, Rocket, ShoppingBag, Layout,
  Facebook, Instagram, Youtube, MessageCircle, Globe2,
  FolderOpen, GripVertical, Minus, Maximize2, Upload, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';

// Campaign Phases
const campaignPhases = [
  { id: 'planning', label: 'Planning', icon: FileText, color: '#6B7280' },
  { id: 'preparation', label: 'Preparation', icon: Settings, color: '#F59E0B' },
  { id: 'prospecting', label: 'Prospecting', icon: Users, color: '#8B5CF6' },
  { id: 'seeding', label: 'Seeding', icon: Sparkles, color: '#06B6D4' },
  { id: 'buzz', label: 'Buzz', icon: Zap, color: '#EC4899' },
  { id: 'launching', label: 'Launching', icon: Megaphone, color: '#F97316' },
  { id: 'running', label: 'Running', icon: Play, color: '#10B981' },
  { id: 'closing', label: 'Closing', icon: Target, color: '#14B8A6' },
];

const allPhasesForTabs = [
  { id: 'prospecting', label: 'Prospecting', color: '#8B5CF6' },
  { id: 'seeding', label: 'Seeding', color: '#06B6D4' },
  { id: 'buzz', label: 'Buzz', color: '#EC4899' },
  { id: 'launching', label: 'Launching', color: '#F97316' },
  { id: 'closing', label: 'Closing', color: '#14B8A6' },
];

const blueprintSteps = [
  { id: 1, key: 'basic', label: 'Basic Info', icon: FileText, color: '#E63946' },
  { id: 2, key: 'objective', label: 'Objective & KPI', icon: Target, color: '#F97316' },
  { id: 3, key: 'team', label: 'Team & PIC', icon: UserCheck, color: '#3B82F6' },
  { id: 4, key: 'offer', label: 'Package & Offer', icon: Package, color: '#F59E0B' },
  { id: 5, key: 'materials', label: 'Launch Materials', icon: FileImage, color: '#10B981' },
  { id: 6, key: 'salespage', label: 'Sales Page Structure', icon: Layout, color: '#8B5CF6' },
  { id: 7, key: 'marketing', label: 'Marketing Strategy', icon: Megaphone, color: '#06B6D4' },
  { id: 8, key: 'adstrategy', label: 'Ad Strategy', icon: Layers, color: '#EF4444' },
  { id: 9, key: 'workflow', label: 'Workflow', icon: Workflow, color: '#EC4899' },
  { id: 10, key: 'phases', label: 'Phase Timeline', icon: Calendar, color: '#A855F7' },
  { id: 11, key: 'budget', label: 'Budget', icon: DollarSign, color: '#22C55E' },
];

const socialPlatforms = [
  { id: 'facebook', label: 'Facebook', icon: Facebook, color: '#1877F2' },
  { id: 'instagram', label: 'Instagram', icon: Instagram, color: '#E4405F' },
  { id: 'tiktok', label: 'TikTok', icon: Play, color: '#000000' },
  { id: 'telegram', label: 'Telegram', icon: MessageCircle, color: '#0088CC' },
  { id: 'youtube', label: 'YouTube', icon: Youtube, color: '#FF0000' },
  { id: 'google', label: 'Google Ads', icon: Globe2, color: '#4285F4' },
];

const currencyOptions = [
  { id: 'RM', label: 'RM', symbol: 'RM' },
  { id: 'USD', label: 'USD', symbol: '$' },
];

const campaignTypes = {
  challenge: { label: 'Challenge/Contest', icon: Target, color: '#E63946' },
  webinar: { label: 'Webinar/Seminar', icon: FileText, color: '#3B82F6' },
  bootcamp: { label: 'Bootcamp/Class', icon: Users, color: '#10B981' },
  promo: { label: 'Promo/Offer', icon: Package, color: '#F59E0B' },
  grow: { label: 'Grow Together', icon: TrendingUp, color: '#8B5CF6' },
  product: { label: 'Product Launching', icon: Rocket, color: '#EC4899' },
  other: { label: 'Other', icon: Megaphone, color: '#6B7280' },
};

const defaultMaterialsByDept = {
  marketing: [
    { name: 'Landing Page', status: 'pending', assignee: '', dueDate: '' },
    { name: 'Sales Video', status: 'pending', assignee: '', dueDate: '' },
    { name: 'Social Media Posts', status: 'pending', assignee: '', dueDate: '' },
    { name: 'Ad Creatives (Static)', status: 'pending', assignee: '', dueDate: '' },
    { name: 'Ad Creatives (Video)', status: 'pending', assignee: '', dueDate: '' },
    { name: 'Email Sequence', status: 'pending', assignee: '', dueDate: '' },
    { name: 'WhatsApp Broadcast Copy', status: 'pending', assignee: '', dueDate: '' },
  ],
  sales: [
    { name: 'Sales Script', status: 'pending', assignee: '', dueDate: '' },
    { name: 'Objection Handling Doc', status: 'pending', assignee: '', dueDate: '' },
    { name: 'Pricing Sheet', status: 'pending', assignee: '', dueDate: '' },
    { name: 'Testimonials Collection', status: 'pending', assignee: '', dueDate: '' },
    { name: 'FAQ Document', status: 'pending', assignee: '', dueDate: '' },
  ],
  operations: [
    { name: 'Registration Form/System', status: 'pending', assignee: '', dueDate: '' },
    { name: 'Payment Gateway Setup', status: 'pending', assignee: '', dueDate: '' },
    { name: 'Member Onboarding Flow', status: 'pending', assignee: '', dueDate: '' },
    { name: 'Support Response Templates', status: 'pending', assignee: '', dueDate: '' },
    { name: 'Tracking & Analytics Setup', status: 'pending', assignee: '', dueDate: '' },
  ],
};

const defaultKPIs = [
  'Leads', 'Conversions', 'Revenue', 'New Subscribers', 'Waiting List', 'Deposit',
  'Impressions', 'Clicks', 'CTR', 'ROI', 'ROAS', 'CPA', 'CPL'
];

// Default Sales Page Sections - RESTORED with examples
const defaultSalesPageSections = {
  prospecting: [
    { section: 'Headline', content: 'Tajuk utama yang menarik perhatian' },
    { section: 'Pain Points', content: 'Masalah yang audience hadapi' },
    { section: 'Teaser', content: 'Preview tentang solusi yang akan datang' },
    { section: 'Lead Magnet CTA', content: 'Free ebook/webinar/checklist' },
  ],
  seeding: [
    { section: 'Headline', content: 'Hook yang kuat untuk tarik perhatian' },
    { section: 'Pre-headline', content: 'Kenyataan pembuka yang relate' },
    { section: 'Main Point', content: 'Mesej utama yang ingin disampaikan' },
    { section: 'Problem', content: 'Masalah yang audience alami' },
    { section: 'Solution Preview', content: 'Bayangan solusi tanpa reveal semua' },
    { section: 'Social Proof', content: 'Testimoni awal atau hasil' },
    { section: 'CTA', content: 'Join waiting list / Follow untuk update' },
  ],
  buzz: [
    { section: 'Countdown Header', content: 'X hari lagi sebelum launch!' },
    { section: 'Headline', content: 'Announcement yang exciting' },
    { section: 'Sneak Peek', content: 'Preview eksklusif produk/offer' },
    { section: 'Social Proof', content: 'Testimoni, achievement, results' },
    { section: 'Early Bird Offer', content: 'Special price untuk yang daftar awal' },
    { section: 'Scarcity', content: 'Limited slots / Limited time' },
    { section: 'CTA', content: 'Secure your spot now!' },
  ],
  launching: [
    { section: 'Hero Section', content: 'Banner utama dengan headline power' },
    { section: 'Video Sales Letter', content: 'Video yang explain offer' },
    { section: 'Who Am I', content: 'Credibility dan background' },
    { section: 'Problem Agitation', content: 'Deep dive into pain points' },
    { section: 'Solution', content: 'Cara produk selesaikan masalah' },
    { section: 'Benefits', content: 'Apa yang akan dapat' },
    { section: 'Package Details', content: 'Apa yang included dalam offer' },
    { section: 'Testimonials', content: 'Proof dan social validation' },
    { section: 'Pricing & Packages', content: 'Pilihan pakej dan harga' },
    { section: 'Bonuses', content: 'Bonus tambahan' },
    { section: 'Guarantee', content: 'Money back guarantee' },
    { section: 'FAQ', content: 'Soalan lazim' },
    { section: 'Final CTA', content: 'Last call to action dengan urgency' },
  ],
  closing: [
    { section: 'Last Chance Header', content: 'Peluang terakhir!' },
    { section: 'Recap Benefits', content: 'Summary apa yang akan dapat' },
    { section: 'Final Testimonials', content: 'Success stories' },
    { section: 'Deadline Reminder', content: 'Tarikh tutup' },
    { section: 'Final CTA', content: 'Buat keputusan sekarang' },
  ],
};

// Default Marketing Strategy Ideas - NEW with 5 examples per phase
const defaultMarketingIdeas = {
  prospecting: [
    { text: 'Content marketing - blog posts tentang pain points audience', done: false },
    { text: 'Lead magnet - free ebook/checklist untuk collect leads', done: false },
    { text: 'Social media awareness posts untuk build audience', done: false },
    { text: 'Collaborate dengan influencers untuk exposure', done: false },
    { text: 'Run traffic ads ke landing page untuk grow list', done: false },
  ],
  seeding: [
    { text: 'Tease upcoming offer melalui email series', done: false },
    { text: 'Behind-the-scenes content di social media', done: false },
    { text: 'Early bird announcement untuk existing subscribers', done: false },
    { text: 'Create anticipation dengan countdown content', done: false },
    { text: 'Engage dengan audience melalui polls/Q&A', done: false },
  ],
  buzz: [
    { text: 'Launch countdown campaign across all platforms', done: false },
    { text: 'Testimonial posts dari beta users/early adopters', done: false },
    { text: 'Live session untuk Q&A tentang offer', done: false },
    { text: 'Scarcity messaging - limited spots available', done: false },
    { text: 'Collaboration posts dengan partners/affiliates', done: false },
  ],
  launching: [
    { text: 'Full launch email sequence (5-7 emails)', done: false },
    { text: 'Heavy ads push dengan retargeting', done: false },
    { text: 'Live webinar/presentation untuk close sales', done: false },
    { text: 'Cart open/close urgency messaging', done: false },
    { text: 'Bonus stack untuk early buyers', done: false },
  ],
  closing: [
    { text: 'Final 48-hour deadline reminder emails', done: false },
    { text: 'Last chance social media posts', done: false },
    { text: 'Personal outreach ke warm leads yang belum convert', done: false },
    { text: 'Final bonus atau special offer untuk fence-sitters', done: false },
    { text: 'Thank you + next steps untuk yang sudah join', done: false },
  ],
};

// Get Default Blueprint Data
const getDefaultBlueprintData = () => ({
  basic: { 
    name: '', 
    type: 'challenge', 
    description: '',
    usp: [
      { point: '' },
      { point: '' },
      { point: '' },
    ]
  },
  objective: {
    mainGoal: '',
    kpis: [
      { metric: 'Leads', target: '' },
      { metric: 'Conversions', target: '' },
      { metric: 'Revenue', target: '' },
      { metric: 'New Subscribers', target: '' },
      { metric: 'Waiting List', target: '' },
      { metric: 'Deposit', target: '' },
    ],
    breakdown: {
      campaign: { leads: '', conversions: '', revenue: '' },
      monthly: { leads: '', conversions: '', revenue: '' },
      weekly: { leads: '', conversions: '', revenue: '' },
      daily: { leads: '', conversions: '', revenue: '' },
    }
  },
  team: {
    members: [
      { role: 'Campaign Lead', name: '' },
      { role: 'Content Creator', name: '' },
      { role: 'Ads Manager', name: '' },
      { role: 'Sales Closer', name: '' },
      { role: 'Customer Support', name: '' },
    ],
  },
  offer: {
    currency: 'RM',
    packages: [
      { name: 'VIP Package', price: '', priceExisting: '', priceNew: '', urgency: '', components: [
        { item: 'Trading Signal Access', included: true },
        { item: 'Private Coaching (1-on-1)', included: true },
        { item: 'Free E-Book', included: true },
        { item: 'Exclusive Merchandise', included: true },
        { item: 'Priority Support', included: true },
      ]},
      { name: 'Gold Package', price: '', priceExisting: '', priceNew: '', urgency: '', components: [
        { item: 'Trading Signal Access', included: true },
        { item: 'Group Coaching', included: true },
        { item: 'Free E-Book', included: true },
      ]},
    ],
  },
  materials: { ...defaultMaterialsByDept },
  salespage: {
    tabs: ['seeding', 'buzz', 'launching'],
    data: {
      seeding: [...defaultSalesPageSections.seeding],
      buzz: [...defaultSalesPageSections.buzz],
      launching: [...defaultSalesPageSections.launching],
    },
  },
  marketing: { ...defaultMarketingIdeas },
  adstrategy: {
    objective: '',
    totalBudget: 0,
    adSets: [
      {
        id: 1,
        name: 'Ad Set 1',
        budget: 0,
        audience: '',
        placement: ['facebook', 'instagram'],
        ads: [
          { id: 1, name: 'Ad 1', format: 'image', copywriting: '', cta: 'Learn More' }
        ]
      }
    ]
  },
  workflow: {
    marketing: { projects: [{ id: 'proj1', name: 'Seeding Flow', nodes: [], connections: [] }], activeProject: 'proj1' },
    sales: { projects: [{ id: 'proj1', name: 'Lead Follow-up', nodes: [], connections: [] }], activeProject: 'proj1' },
    operations: { projects: [{ id: 'proj1', name: 'Onboarding Flow', nodes: [], connections: [] }], activeProject: 'proj1' },
  },
  phases: {
    timeline: campaignPhases.map(phase => ({
      id: phase.id, label: phase.label, startDate: '', endDate: '', status: 'pending',
    })),
  },
  budget: {
    totalBudget: 0,
    dailySpend: 0,
    campaignDays: 30,
    byPhase: {
      seeding: socialPlatforms.map(p => ({ platform: p.id, allocated: 0 })),
      buzz: socialPlatforms.map(p => ({ platform: p.id, allocated: 0 })),
      launching: socialPlatforms.map(p => ({ platform: p.id, allocated: 0 })),
    },
  },
});

const getCurrentPhase = (phases) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  for (const phase of phases) {
    if (phase.status === 'running') return phase;
    if (phase.startDate && phase.endDate) {
      const start = new Date(phase.startDate);
      const end = new Date(phase.endDate);
      if (today >= start && today <= end) return phase;
    }
  }
  return phases.find(p => p.status === 'pending' && p.startDate) || phases[0];
};

// Phase Timeline Visual - With days remaining BETWEEN phases
function PhaseTimelineVisual({ phases, currentCampaignStatus }) {
  const visiblePhases = phases.filter(p => p.status !== 'skipped');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const currentPhaseIdx = visiblePhases.findIndex(p => p.id === currentCampaignStatus);
  
  // Calculate days from TODAY to each phase's START date
  const getDaysUntilPhase = (phase) => {
    if (!phase.startDate) return null;
    const start = new Date(phase.startDate);
    start.setHours(0, 0, 0, 0);
    const diff = Math.ceil((start - today) / (1000 * 60 * 60 * 24));
    // Only show countdown for future phases
    if (diff <= 0) return null;
    return diff;
  };
  
  return (
    <div className="relative py-4 px-4">
      {/* Progress line */}
      <div className="absolute top-[50px] left-[5%] right-[5%] h-1.5 bg-[hsl(var(--border))] rounded-full" />
      {currentPhaseIdx >= 0 && (
        <div className="absolute top-[50px] left-[5%] h-1.5 bg-gradient-to-r from-[#10B981] via-[#06B6D4] to-[#3B82F6] rounded-full transition-all duration-500"
          style={{ width: `${Math.min((currentPhaseIdx / (visiblePhases.length - 1)) * 90, 90)}%` }} />
      )}
      
      {/* Phase nodes */}
      <div className="relative flex justify-between mt-2">
        {visiblePhases.map((phase, idx) => {
          const phaseConfig = campaignPhases.find(p => p.id === phase.id);
          const isCompleted = phase.status === 'completed';
          const isRunning = phase.status === 'running';
          const hasDates = phase.startDate && phase.endDate;
          const isCurrentStatus = phase.id === currentCampaignStatus;
          const isPast = idx < currentPhaseIdx;
          const daysUntil = getDaysUntilPhase(phase);
          
          return (
            <React.Fragment key={phase.id}>
              <div className="flex flex-col items-center relative" style={{ minWidth: '70px' }}>
                {isCurrentStatus && (
                  <div className="absolute -top-6 left-1/2 -translate-x-1/2 whitespace-nowrap z-10">
                    <span className="text-[10px] bg-gradient-to-r from-[#E63946] to-[#F97316] text-white px-2 py-0.5 rounded-full shadow font-semibold">
                      📍 Anda di sini
                    </span>
                  </div>
                )}
                
                {/* Days countdown badge - shows above phase node */}
                {daysUntil !== null && !isCompleted && !isRunning && !isPast && (
                  <div className="absolute -top-6 left-1/2 -translate-x-1/2 whitespace-nowrap">
                    <span className="text-[9px] text-[#F59E0B] font-medium bg-[#F59E0B]/10 px-1.5 py-0.5 rounded">
                      {daysUntil}d lagi
                    </span>
                  </div>
                )}
                
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center z-10 transition-all shadow-lg ${
                  isCompleted ? 'bg-gradient-to-br from-[#10B981] to-[#059669]' :
                  isRunning ? 'bg-gradient-to-br from-[#F59E0B] to-[#D97706]' :
                  isCurrentStatus ? 'bg-gradient-to-br from-[#E63946] to-[#DC2626] ring-2 ring-[#E63946]/30' :
                  isPast ? 'bg-[#10B981]' : hasDates ? 'bg-[hsl(var(--surface-elevated))]' : 'bg-[hsl(var(--surface))]'
                }`}
                  style={{ 
                    backgroundColor: !isCompleted && !isRunning && !isCurrentStatus && !isPast && hasDates 
                      ? `${phaseConfig?.color}25` : undefined,
                    borderColor: hasDates && !isCompleted && !isRunning && !isCurrentStatus && !isPast 
                      ? phaseConfig?.color : undefined,
                    borderWidth: hasDates && !isCompleted && !isRunning && !isCurrentStatus && !isPast ? '2px' : undefined
                  }}>
                  {isCompleted || isPast ? <Check size={18} className="text-white" /> :
                   isRunning ? <Play size={16} className="text-white" /> :
                   React.createElement(phaseConfig?.icon || Circle, { 
                     size: 16, style: { color: isCurrentStatus ? 'white' : hasDates ? phaseConfig?.color : '#6B7280' } 
                   })}
                </div>
                
                <p className={`text-[10px] mt-2 text-center font-semibold ${
                  isCompleted || isPast ? 'text-[#10B981]' : 
                  isRunning ? 'text-[#F59E0B]' : 
                  isCurrentStatus ? 'text-[#E63946]' : hasDates ? 'text-white' : 'text-[#6B7280]'
                }`}>{phase.label}</p>
                
                {hasDates && (
                  <p className="text-[8px] text-[#9CA3AF] text-center">
                    {new Date(phase.startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                  </p>
                )}
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

// Step Indicator
function StepIndicator({ steps, currentStep, onStepClick }) {
  return (
    <div className="flex items-center gap-1 overflow-x-auto pb-2">
      {steps.map((step, idx) => {
        const isCompleted = idx + 1 < currentStep;
        const isCurrent = idx + 1 === currentStep;
        const Icon = step.icon;
        return (
          <button key={step.id} onClick={() => onStepClick(idx + 1)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all whitespace-nowrap ${
              isCurrent ? 'bg-[#E63946]/20 ring-1 ring-[#E63946]' : 
              isCompleted ? 'bg-[#10B981]/20' : 'bg-[hsl(var(--surface-elevated))] hover:bg-[hsl(var(--surface))]'
            }`}>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center ${isCompleted ? 'bg-[#10B981]' : ''}`}
                 style={{ backgroundColor: isCurrent ? `${step.color}30` : undefined }}>
              {isCompleted ? <Check size={12} className="text-white" /> : 
               <Icon size={12} style={{ color: isCurrent ? step.color : '#6B7280' }} />}
            </div>
            <span className={`text-xs ${isCurrent ? 'text-white font-semibold' : 'text-[#9CA3AF]'}`}>{step.label}</span>
          </button>
        );
      })}
    </div>
  );
}

// Materials Checklist
function MaterialsChecklist({ materials, onUpdate, onDelete }) {
  const departments = [
    { key: 'marketing', label: 'Marketing', icon: Megaphone, color: '#E63946' },
    { key: 'sales', label: 'Sales', icon: ShoppingBag, color: '#3B82F6' },
    { key: 'operations', label: 'Operations', icon: Settings, color: '#10B981' },
  ];
  const getProgress = (items) => ({ done: items.filter(i => i.status === 'done').length, total: items.length });

  return (
    <Tabs defaultValue="marketing" className="w-full">
      <TabsList className="w-full bg-[hsl(var(--surface-elevated))] p-1 mb-4">
        {departments.map(dept => {
          const prog = getProgress(materials[dept.key] || []);
          return (
            <TabsTrigger key={dept.key} value={dept.key}
              className="flex-1 data-[state=active]:bg-[hsl(var(--surface))] data-[state=active]:text-white">
              <dept.icon size={14} className="mr-1.5" style={{ color: dept.color }} />
              {dept.label}
              <Badge className="ml-2 text-[10px]" style={{ backgroundColor: `${dept.color}20`, color: dept.color }}>
                {prog.done}/{prog.total}
              </Badge>
            </TabsTrigger>
          );
        })}
      </TabsList>
      {departments.map(dept => (
        <TabsContent key={dept.key} value={dept.key} className="space-y-2">
          {(materials[dept.key] || []).map((item, idx) => (
            <div key={idx} className="flex items-center gap-3 p-3 bg-[hsl(var(--surface-elevated))] rounded-lg border-l-3" 
                 style={{ borderLeftColor: dept.color }}>
              <Checkbox checked={item.status === 'done'}
                onCheckedChange={(checked) => onUpdate(dept.key, idx, 'status', checked ? 'done' : 'pending')} />
              <Input value={item.name} onChange={(e) => onUpdate(dept.key, idx, 'name', e.target.value)}
                className="flex-1 bg-transparent border-none text-white p-0 h-auto text-sm" />
              <Input value={item.assignee} onChange={(e) => onUpdate(dept.key, idx, 'assignee', e.target.value)}
                placeholder="PIC" className="w-28 bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white text-sm" />
              <Input type="date" value={item.dueDate} onChange={(e) => onUpdate(dept.key, idx, 'dueDate', e.target.value)}
                className="w-36 bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white text-sm [color-scheme:dark]" />
              <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-[#E63946] hover:bg-[#E63946]/20"
                onClick={() => onDelete(dept.key, idx)}><Trash2 size={16} /></Button>
            </div>
          ))}
          <Button size="sm" variant="outline" className="w-full border-dashed border-[hsl(var(--border))] text-[#9CA3AF]"
            onClick={() => onUpdate(dept.key, -1, 'add', { name: '', status: 'pending', assignee: '', dueDate: '' })}>
            <Plus size={14} className="mr-1" />Add Item
          </Button>
        </TabsContent>
      ))}
    </Tabs>
  );
}

// Package Builder
function PackageBuilder({ packages, currency, onUpdate, onCurrencyChange }) {
  const currencySymbol = currency === 'USD' ? '$' : 'RM';
  const addPackage = () => onUpdate([...packages, { name: 'New Package', price: '', priceExisting: '', priceNew: '', urgency: '', components: [] }]);
  const updatePackage = (idx, field, value) => { const u = [...packages]; u[idx] = { ...u[idx], [field]: value }; onUpdate(u); };
  const deletePackage = (idx) => onUpdate(packages.filter((_, i) => i !== idx));
  const addComponent = (pkgIdx) => { const u = [...packages]; u[pkgIdx].components = [...u[pkgIdx].components, { item: '', included: true }]; onUpdate(u); };
  const updateComponent = (pkgIdx, compIdx, field, value) => { const u = [...packages]; u[pkgIdx].components[compIdx] = { ...u[pkgIdx].components[compIdx], [field]: value }; onUpdate(u); };
  const deleteComponent = (pkgIdx, compIdx) => { const u = [...packages]; u[pkgIdx].components = u[pkgIdx].components.filter((_, i) => i !== compIdx); onUpdate(u); };
  const packageColors = ['#E63946', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6', '#EC4899'];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 p-3 bg-[hsl(var(--surface-elevated))] rounded-lg">
        <Label className="text-[#9CA3AF] text-sm">Currency:</Label>
        <Select value={currency} onValueChange={onCurrencyChange}>
          <SelectTrigger className="w-28 bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
            {currencyOptions.map(c => (<SelectItem key={c.id} value={c.id} className="text-white">{c.label}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>
      {packages.map((pkg, pkgIdx) => {
        const color = packageColors[pkgIdx % packageColors.length];
        return (
          <div key={pkgIdx} className="p-4 bg-[hsl(var(--surface-elevated))] rounded-xl border-l-4" style={{ borderColor: color }}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3 flex-1">
                <Package size={20} style={{ color }} />
                <Input value={pkg.name} onChange={(e) => updatePackage(pkgIdx, 'name', e.target.value)}
                  placeholder="Nama Pakej" className="flex-1 bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white font-semibold" />
              </div>
              <Button size="sm" variant="ghost" className="ml-2 text-[#E63946] hover:bg-[#E63946]/20" onClick={() => deletePackage(pkgIdx)}><Trash2 size={18} /></Button>
            </div>
            
            {/* Pricing Section - Aligned properly */}
            <div className="grid grid-cols-3 gap-4 mb-4 p-4 bg-[hsl(var(--surface))] rounded-xl border border-[hsl(var(--border))]">
              <div className="space-y-2">
                <Label className="text-[10px] text-[#9CA3AF] uppercase tracking-wider flex items-center gap-1">
                  <DollarSign size={10} />
                  Normal Price
                </Label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[#9CA3AF] font-medium">{currencySymbol}</span>
                  <Input 
                    type="number"
                    value={pkg.price || ''} 
                    onChange={(e) => updatePackage(pkgIdx, 'price', e.target.value)}
                    placeholder="0"
                    className="flex-1 h-10 bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white font-bold text-lg"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-[10px] text-[#10B981] uppercase tracking-wider flex items-center gap-1">
                  <Users size={10} />
                  Existing Client
                </Label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[#10B981] font-medium">{currencySymbol}</span>
                  <Input 
                    type="number"
                    value={pkg.priceExisting || ''} 
                    onChange={(e) => updatePackage(pkgIdx, 'priceExisting', e.target.value)}
                    placeholder="0"
                    className="flex-1 h-10 bg-[#10B981]/10 border-[#10B981]/30 text-[#10B981] font-bold text-lg"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-[10px] text-[#3B82F6] uppercase tracking-wider flex items-center gap-1">
                  <UserCheck size={10} />
                  New Client
                </Label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[#3B82F6] font-medium">{currencySymbol}</span>
                  <Input 
                    type="number"
                    value={pkg.priceNew || ''} 
                    onChange={(e) => updatePackage(pkgIdx, 'priceNew', e.target.value)}
                    placeholder="0"
                    className="flex-1 h-10 bg-[#3B82F6]/10 border-[#3B82F6]/30 text-[#3B82F6] font-bold text-lg"
                  />
                </div>
              </div>
            </div>
            
            <div className="space-y-2 ml-6 mb-4">
              <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wider mb-2">Komponen / Breakdown</p>
              {pkg.components.map((comp, compIdx) => (
                <div key={compIdx} className="flex items-center gap-2 p-2 bg-[hsl(var(--surface))] rounded-lg">
                  <Checkbox checked={comp.included} onCheckedChange={(checked) => updateComponent(pkgIdx, compIdx, 'included', checked)} />
                  <Input value={comp.item} onChange={(e) => updateComponent(pkgIdx, compIdx, 'item', e.target.value)}
                    placeholder="Item komponen..." className="flex-1 bg-transparent border-none text-white text-sm h-auto p-0" />
                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-[#E63946]/60 hover:text-[#E63946]" onClick={() => deleteComponent(pkgIdx, compIdx)}><X size={14} /></Button>
                </div>
              ))}
              <Button size="sm" variant="ghost" className="text-xs text-[#9CA3AF] hover:text-white" onClick={() => addComponent(pkgIdx)}>
                <Plus size={14} className="mr-1" />Tambah Komponen
              </Button>
            </div>
            <div className="mt-3 pt-3 border-t border-[hsl(var(--border))]">
              <Label className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">Urgency / Scarcity</Label>
              <Input value={pkg.urgency || ''} onChange={(e) => updatePackage(pkgIdx, 'urgency', e.target.value)}
                placeholder="e.g., Terhad 50 slot sahaja! atau Early Bird until 20 Jan"
                className="mt-1 bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white text-sm" />
            </div>
          </div>
        );
      })}
      <Button variant="outline" className="w-full border-dashed border-[hsl(var(--border))] text-[#9CA3AF]" onClick={addPackage}>
        <Plus size={16} className="mr-2" />Tambah Pakej Baru
      </Button>
    </div>
  );
}

// Sales Page Structure - With restored examples
function SalesPageStructure({ salespage, onUpdate }) {
  const activeTabs = salespage?.tabs || ['seeding', 'buzz', 'launching'];
  const data = salespage?.data || {};
  const [activeTab, setActiveTab] = useState(activeTabs[0] || 'seeding');

  const addTab = (phaseId) => {
    if (!phaseId || activeTabs.includes(phaseId)) return;
    const defaultSections = defaultSalesPageSections[phaseId] || [];
    onUpdate({ tabs: [...activeTabs, phaseId], data: { ...data, [phaseId]: [...defaultSections] } });
    setActiveTab(phaseId);
  };
  const removeTab = (phaseId) => {
    if (activeTabs.length <= 1) return;
    const newTabs = activeTabs.filter(t => t !== phaseId);
    const newData = { ...data }; delete newData[phaseId];
    onUpdate({ tabs: newTabs, data: newData });
    if (activeTab === phaseId) setActiveTab(newTabs[0]);
  };
  const addSection = (phaseKey) => onUpdate({ tabs: activeTabs, data: { ...data, [phaseKey]: [...(data[phaseKey] || []), { section: '', content: '' }] } });
  const updateSection = (phaseKey, idx, field, value) => {
    const newData = { ...data }; newData[phaseKey] = [...(newData[phaseKey] || [])];
    newData[phaseKey][idx] = { ...newData[phaseKey][idx], [field]: value };
    onUpdate({ tabs: activeTabs, data: newData });
  };
  const deleteSection = (phaseKey, idx) => onUpdate({ tabs: activeTabs, data: { ...data, [phaseKey]: (data[phaseKey] || []).filter((_, i) => i !== idx) } });
  const availableToAdd = allPhasesForTabs.filter(p => !activeTabs.includes(p.id));

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        {activeTabs.map(tabId => {
          const phase = allPhasesForTabs.find(p => p.id === tabId);
          const isActive = activeTab === tabId;
          return (
            <div key={tabId} className={`flex items-center gap-1 px-3 py-2 rounded-lg cursor-pointer transition-all ${
              isActive ? 'bg-[hsl(var(--surface))] ring-1' : 'bg-[hsl(var(--surface-elevated))]'
            }`} style={{ ringColor: isActive ? phase?.color : undefined }} onClick={() => setActiveTab(tabId)}>
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: phase?.color }} />
              <span className={`text-sm ${isActive ? 'text-white font-semibold' : 'text-[#9CA3AF]'}`}>{phase?.label}</span>
              <Badge className="text-[9px] ml-1" style={{ backgroundColor: `${phase?.color}20`, color: phase?.color }}>
                {(data[tabId] || []).length}
              </Badge>
              {activeTabs.length > 1 && (
                <button onClick={(e) => { e.stopPropagation(); removeTab(tabId); }} className="ml-1 text-[#E63946]/50 hover:text-[#E63946]"><X size={12} /></button>
              )}
            </div>
          );
        })}
        {availableToAdd.length > 0 && (
          <Select onValueChange={addTab}>
            <SelectTrigger className="w-auto h-9 px-3 bg-[hsl(var(--surface-elevated))] border-dashed border-[hsl(var(--border))] text-[#9CA3AF]">
              <Plus size={14} className="mr-1" /><span className="text-sm">Tab</span>
            </SelectTrigger>
            <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
              {availableToAdd.map(phase => (
                <SelectItem key={phase.id} value={phase.id} className="text-white">
                  <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: phase.color }} />{phase.label}</div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      <div className="space-y-3">
        <p className="text-sm text-[#9CA3AF]">
          Design struktur landing page untuk fasa <strong className="text-white">{allPhasesForTabs.find(p => p.id === activeTab)?.label}</strong>
        </p>
        {(data[activeTab] || []).map((section, idx) => (
          <div key={idx} className="flex items-start gap-3 p-3 bg-[hsl(var(--surface-elevated))] rounded-lg group">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[hsl(var(--surface))] text-[#9CA3AF] text-sm font-bold mt-1">{idx + 1}</div>
            <div className="flex-1 space-y-2">
              <Input value={section.section} onChange={(e) => updateSection(activeTab, idx, 'section', e.target.value)}
                placeholder="Nama Section" className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white font-medium" />
              <Textarea value={section.content} onChange={(e) => updateSection(activeTab, idx, 'content', e.target.value)}
                placeholder="Nota atau content..." className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white text-sm min-h-[60px]" />
            </div>
            <Button size="sm" variant="ghost" className="opacity-0 group-hover:opacity-100 text-[#E63946] hover:bg-[#E63946]/20"
              onClick={() => deleteSection(activeTab, idx)}><Trash2 size={16} /></Button>
          </div>
        ))}
        <Button variant="outline" className="w-full border-dashed border-[hsl(var(--border))] text-[#9CA3AF]" onClick={() => addSection(activeTab)}>
          <Plus size={14} className="mr-1" />Tambah Section
        </Button>
      </div>
    </div>
  );
}

// Marketing Strategy - With default ideas
function MarketingStrategy({ marketing, onUpdate }) {
  const phases = [
    { key: 'prospecting', label: 'Prospecting', color: '#8B5CF6', icon: Users },
    { key: 'seeding', label: 'Seeding', color: '#06B6D4', icon: Sparkles },
    { key: 'buzz', label: 'Buzz', color: '#EC4899', icon: Zap },
    { key: 'launching', label: 'Launching', color: '#F97316', icon: Megaphone },
    { key: 'closing', label: 'Closing', color: '#14B8A6', icon: Target },
  ];
  const addItem = (phaseKey) => onUpdate({ ...marketing, [phaseKey]: [...(marketing[phaseKey] || []), { text: '', done: false }] });
  const updateItem = (phaseKey, idx, field, value) => {
    const u = { ...marketing }; u[phaseKey] = [...u[phaseKey]]; u[phaseKey][idx] = { ...u[phaseKey][idx], [field]: value }; onUpdate(u);
  };
  const deleteItem = (phaseKey, idx) => onUpdate({ ...marketing, [phaseKey]: marketing[phaseKey].filter((_, i) => i !== idx) });

  return (
    <Tabs defaultValue="prospecting" className="w-full">
      <TabsList className="w-full bg-[hsl(var(--surface-elevated))] p-1 mb-4 flex-wrap h-auto gap-1">
        {phases.map(phase => (
          <TabsTrigger key={phase.key} value={phase.key}
            className="flex-1 min-w-[90px] data-[state=active]:bg-[hsl(var(--surface))] data-[state=active]:text-white py-2">
            <phase.icon size={12} className="mr-1" style={{ color: phase.color }} />
            <span className="text-xs">{phase.label}</span>
            {(marketing[phase.key] || []).length > 0 && (
              <Badge className="ml-1 text-[8px] h-4 px-1" style={{ backgroundColor: `${phase.color}20`, color: phase.color }}>
                {(marketing[phase.key] || []).length}
              </Badge>
            )}
          </TabsTrigger>
        ))}
      </TabsList>
      {phases.map(phase => (
        <TabsContent key={phase.key} value={phase.key} className="space-y-2">
          <div className="p-3 rounded-lg mb-3" style={{ backgroundColor: `${phase.color}10`, borderLeft: `3px solid ${phase.color}` }}>
            <p className="text-sm text-[#9CA3AF]">Senaraikan idea dan checklist strategi untuk fasa <strong style={{ color: phase.color }}>{phase.label}</strong></p>
          </div>
          {(marketing[phase.key] || []).map((item, idx) => (
            <div key={idx} className="flex items-center gap-3 p-3 bg-[hsl(var(--surface-elevated))] rounded-lg group">
              <Checkbox checked={item.done} onCheckedChange={(checked) => updateItem(phase.key, idx, 'done', checked)} />
              <Input value={item.text} onChange={(e) => updateItem(phase.key, idx, 'text', e.target.value)} placeholder="Tulis idea..."
                className={`flex-1 bg-transparent border-none text-sm p-0 h-auto ${item.done ? 'text-[#6B7280] line-through' : 'text-white'}`} />
              <Button size="sm" variant="ghost" className="opacity-0 group-hover:opacity-100 h-6 w-6 p-0 text-[#E63946]" onClick={() => deleteItem(phase.key, idx)}><Trash2 size={14} /></Button>
            </div>
          ))}
          <Button variant="outline" className="w-full border-dashed border-[hsl(var(--border))] text-[#9CA3AF]" onClick={() => addItem(phase.key)}>
            <Plus size={14} className="mr-1" />Tambah Idea
          </Button>
        </TabsContent>
      ))}
    </Tabs>
  );
}

// ============================================
// AD STRATEGY BUILDER - Flowchart/Tree Layout with Multiple Campaigns
// ============================================
function AdStrategyBuilder({ adstrategy, onUpdate }) {
  // Support multiple campaigns
  const campaigns = adstrategy?.campaigns || [{ 
    id: 1, 
    name: 'Campaign 1', 
    objective: adstrategy?.objective || '',
    adSets: adstrategy?.adSets || []
  }];
  
  const [activeCampaignId, setActiveCampaignId] = useState(campaigns[0]?.id || 1);
  const activeCampaign = campaigns.find(c => c.id === activeCampaignId) || campaigns[0];
  
  const [expandedAdSets, setExpandedAdSets] = useState(() => 
    (activeCampaign?.adSets || []).map(a => a.id)
  );

  // Migrate old format to new format
  useEffect(() => {
    if (!adstrategy?.campaigns && adstrategy?.adSets) {
      onUpdate({
        campaigns: [{
          id: 1,
          name: 'Campaign 1',
          objective: adstrategy.objective || '',
          adSets: adstrategy.adSets || []
        }]
      });
    }
  }, []);

  const addCampaign = () => {
    const newId = Date.now();
    const newCampaigns = [...campaigns, {
      id: newId,
      name: `Campaign ${campaigns.length + 1}`,
      objective: '',
      adSets: [{
        id: newId + 1,
        name: 'Ad Set 1',
        budget: 0,
        audience: '',
        placement: ['facebook', 'instagram'],
        ads: [{ id: newId + 2, name: 'Ad 1', format: 'image', copywriting: '', cta: 'Learn More' }]
      }]
    }];
    onUpdate({ campaigns: newCampaigns });
    setActiveCampaignId(newId);
  };

  const deleteCampaign = (campaignId) => {
    if (campaigns.length <= 1) return;
    const newCampaigns = campaigns.filter(c => c.id !== campaignId);
    onUpdate({ campaigns: newCampaigns });
    if (activeCampaignId === campaignId) {
      setActiveCampaignId(newCampaigns[0]?.id);
    }
  };

  const updateCampaign = (campaignId, field, value) => {
    const newCampaigns = campaigns.map(c => 
      c.id === campaignId ? { ...c, [field]: value } : c
    );
    onUpdate({ campaigns: newCampaigns });
  };

  const toggleAdSet = (id) => {
    setExpandedAdSets(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const addAdSet = () => {
    const newId = Date.now();
    const newAdSets = [...(activeCampaign?.adSets || []), {
      id: newId,
      name: `Ad Set ${(activeCampaign?.adSets?.length || 0) + 1}`,
      budget: 0,
      audience: '',
      placement: ['facebook', 'instagram'],
      ads: [{ id: newId + 1, name: 'Ad 1', format: 'image', copywriting: '', cta: 'Learn More' }]
    }];
    updateCampaign(activeCampaignId, 'adSets', newAdSets);
    setExpandedAdSets(prev => [...prev, newId]);
  };

  const deleteAdSet = (adSetId) => {
    const newAdSets = (activeCampaign?.adSets || []).filter(a => a.id !== adSetId);
    updateCampaign(activeCampaignId, 'adSets', newAdSets);
  };

  const updateAdSet = (adSetId, field, value) => {
    const newAdSets = (activeCampaign?.adSets || []).map(a => 
      a.id === adSetId ? { ...a, [field]: value } : a
    );
    updateCampaign(activeCampaignId, 'adSets', newAdSets);
  };

  const addAd = (adSetId) => {
    const newAdSets = (activeCampaign?.adSets || []).map(a => 
      a.id === adSetId ? { 
        ...a, 
        ads: [...(a.ads || []), { 
          id: Date.now(), 
          name: `Ad ${(a.ads?.length || 0) + 1}`, 
          format: 'image', 
          copywriting: '', 
          cta: 'Learn More' 
        }]
      } : a
    );
    updateCampaign(activeCampaignId, 'adSets', newAdSets);
  };

  const deleteAd = (adSetId, adId) => {
    const newAdSets = (activeCampaign?.adSets || []).map(a => 
      a.id === adSetId ? { ...a, ads: (a.ads || []).filter(ad => ad.id !== adId) } : a
    );
    updateCampaign(activeCampaignId, 'adSets', newAdSets);
  };

  const updateAd = (adSetId, adId, field, value) => {
    const newAdSets = (activeCampaign?.adSets || []).map(a => 
      a.id === adSetId ? { 
        ...a, 
        ads: (a.ads || []).map(ad => ad.id === adId ? { ...ad, [field]: value } : ad)
      } : a
    );
    updateCampaign(activeCampaignId, 'adSets', newAdSets);
  };

  const togglePlacement = (adSetId, placement) => {
    const adSet = (activeCampaign?.adSets || []).find(a => a.id === adSetId);
    const currentPlacements = adSet?.placement || [];
    const newPlacements = currentPlacements.includes(placement)
      ? currentPlacements.filter(p => p !== placement)
      : [...currentPlacements, placement];
    updateAdSet(adSetId, 'placement', newPlacements);
  };

  const formatOptions = [
    { id: 'image', label: 'Image', icon: FileImage },
    { id: 'video', label: 'Video', icon: Play },
    { id: 'carousel', label: 'Carousel', icon: Layers },
  ];

  const ctaOptions = ['Learn More', 'Sign Up', 'Shop Now', 'Contact Us', 'Get Offer', 'Book Now', 'Subscribe', 'Download'];

  const totalAds = (activeCampaign?.adSets || []).reduce((sum, a) => sum + (a.ads?.length || 0), 0);
  const totalBudget = (activeCampaign?.adSets || []).reduce((sum, a) => sum + (parseFloat(a.budget) || 0), 0);
  const grandTotalAds = campaigns.reduce((sum, c) => sum + (c.adSets || []).reduce((s, a) => s + (a.ads?.length || 0), 0), 0);

  return (
    <div className="space-y-4">
      {/* Campaign Tabs */}
      <div className="flex items-center gap-2 p-2 bg-[hsl(var(--surface-elevated))] rounded-xl border border-[hsl(var(--border))]">
        <div className="flex items-center gap-1 flex-1 overflow-x-auto">
          {campaigns.map((campaign, idx) => (
            <div 
              key={campaign.id}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-all group ${
                activeCampaignId === campaign.id 
                  ? 'bg-[#10B981]/20 border border-[#10B981]' 
                  : 'hover:bg-[hsl(var(--surface))] border border-transparent'
              }`}
              onClick={() => setActiveCampaignId(campaign.id)}
            >
              <Target size={14} className={activeCampaignId === campaign.id ? 'text-[#10B981]' : 'text-[#6B7280]'} />
              <Input 
                value={campaign.name}
                onChange={(e) => { e.stopPropagation(); updateCampaign(campaign.id, 'name', e.target.value); }}
                onClick={(e) => e.stopPropagation()}
                className={`bg-transparent border-none p-0 h-auto text-sm font-medium w-28 ${
                  activeCampaignId === campaign.id ? 'text-white' : 'text-[#9CA3AF]'
                }`}
              />
              {campaigns.length > 1 && (
                <Button 
                  size="icon" 
                  variant="ghost" 
                  className="h-5 w-5 opacity-0 group-hover:opacity-100 text-[#E63946]"
                  onClick={(e) => { e.stopPropagation(); deleteCampaign(campaign.id); }}
                >
                  <X size={12} />
                </Button>
              )}
            </div>
          ))}
        </div>
        <Button 
          size="sm" 
          variant="outline" 
          className="border-dashed border-[#10B981]/50 text-[#10B981] hover:bg-[#10B981]/10 shrink-0"
          onClick={addCampaign}
        >
          <Plus size={14} className="mr-1" /> Campaign Baru
        </Button>
      </div>

      {/* Active Campaign Content */}
      <div className="space-y-4">
        {/* Campaign Level Header */}
        <div className="flex justify-center">
          <div className="bg-gradient-to-r from-[#10B981]/20 via-[#10B981]/10 to-[#10B981]/20 border-2 border-[#10B981] rounded-xl p-4 w-full max-w-lg shadow-[0_0_30px_-10px_#10B981]">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-[#10B981]/30 flex items-center justify-center">
                  <Target size={18} className="text-[#10B981]" />
                </div>
                <div>
                  <span className="font-bold text-white">{activeCampaign?.name || 'Campaign'}</span>
                  <p className="text-[10px] text-[#6B7280]">Campaign {campaigns.findIndex(c => c.id === activeCampaignId) + 1} of {campaigns.length}</p>
                </div>
              </div>
              <Badge className="bg-[#10B981] text-white shadow-lg">{campaigns.length} Campaigns</Badge>
            </div>
            <div className="space-y-2">
              <div>
                <Label className="text-[10px] text-[#6B7280]">Campaign Objective</Label>
                <Input 
                  value={activeCampaign?.objective || ''} 
                  onChange={(e) => updateCampaign(activeCampaignId, 'objective', e.target.value)}
                  placeholder="e.g., Lead Generation, Brand Awareness..."
                  className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white text-sm"
                />
              </div>
              <div className="grid grid-cols-3 gap-2 pt-2 border-t border-[#10B981]/30">
                <div className="text-center p-2 bg-[hsl(var(--surface))]/50 rounded-lg">
                  <p className="text-[10px] text-[#6B7280]">Ad Sets</p>
                  <p className="text-lg font-bold text-white">{(activeCampaign?.adSets || []).length}</p>
                </div>
                <div className="text-center p-2 bg-[hsl(var(--surface))]/50 rounded-lg">
                  <p className="text-[10px] text-[#6B7280]">Total Ads</p>
                  <p className="text-lg font-bold text-white">{totalAds}</p>
                </div>
                <div className="text-center p-2 bg-[hsl(var(--surface))]/50 rounded-lg">
                  <p className="text-[10px] text-[#6B7280]">Budget</p>
                  <p className="text-lg font-bold text-[#10B981]">RM {totalBudget.toLocaleString()}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Vertical Line */}
        <div className="flex justify-center">
          <div className="w-0.5 h-6 bg-gradient-to-b from-[#10B981] to-[#3B82F6]" />
        </div>

        {/* Ad Sets Grid */}
        <div className="grid grid-cols-2 gap-4">
          {(activeCampaign?.adSets || []).map((adSet, idx) => (
            <div key={adSet.id} className="relative">
              {/* Ad Set Box */}
              <div className="bg-[#3B82F6]/10 border-2 border-[#3B82F6] rounded-xl overflow-hidden shadow-[0_0_20px_-8px_#3B82F6]">
                {/* Ad Set Header */}
                <div 
                  className="p-3 border-b border-[#3B82F6]/30 cursor-pointer hover:bg-[#3B82F6]/10 bg-gradient-to-r from-[#3B82F6]/20 to-transparent"
                  onClick={() => toggleAdSet(adSet.id)}
                >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Layers size={16} className="text-[#3B82F6]" />
                    <Input 
                      value={adSet.name}
                      onChange={(e) => { e.stopPropagation(); updateAdSet(adSet.id, 'name', e.target.value); }}
                      onClick={(e) => e.stopPropagation()}
                      className="bg-transparent border-none p-0 h-auto text-sm font-bold text-white w-32"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-[#3B82F6] text-white text-xs">{adSet.ads?.length || 0} ads</Badge>
                    <Button size="icon" variant="ghost" className="h-6 w-6 text-[#E63946]" onClick={(e) => { e.stopPropagation(); deleteAdSet(adSet.id); }}>
                      <Trash2 size={14} />
                    </Button>
                    {expandedAdSets.includes(adSet.id) ? <ChevronDown size={16} className="text-[#3B82F6]" /> : <ChevronRight size={16} className="text-[#3B82F6]" />}
                  </div>
                </div>
                
                {/* Ad Set Fields */}
                <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-2">
                    <DollarSign size={12} className="text-[#6B7280]" />
                    <span className="text-xs text-[#6B7280]">Budget:</span>
                    <Input 
                      type="number"
                      value={adSet.budget || ''}
                      onChange={(e) => updateAdSet(adSet.id, 'budget', e.target.value)}
                      placeholder="0"
                      className="w-24 h-7 bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white text-xs"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Users size={12} className="text-[#6B7280]" />
                    <span className="text-xs text-[#6B7280]">Audience:</span>
                    <Input 
                      value={adSet.audience || ''}
                      onChange={(e) => updateAdSet(adSet.id, 'audience', e.target.value)}
                      placeholder="Interest, Lookalike..."
                      className="flex-1 h-7 bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white text-xs"
                    />
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Globe2 size={12} className="text-[#6B7280]" />
                    <span className="text-xs text-[#6B7280]">Placement:</span>
                    {socialPlatforms.slice(0, 4).map(p => (
                      <button
                        key={p.id}
                        onClick={() => togglePlacement(adSet.id, p.id)}
                        className={`px-2 py-0.5 rounded text-[10px] transition-all ${
                          (adSet.placement || []).includes(p.id)
                            ? 'bg-[#3B82F6] text-white'
                            : 'bg-[hsl(var(--surface))] text-[#6B7280] hover:text-white'
                        }`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Ads - Expanded */}
              {expandedAdSets.includes(adSet.id) && (
                <div className="p-3 space-y-2 bg-[#0a0a0c]">
                  {(adSet.ads || []).map((ad) => (
                    <div 
                      key={ad.id}
                      className="bg-[#F97316]/10 border border-[#F97316]/50 rounded-lg p-3"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {ad.format === 'video' ? <Play size={14} className="text-[#F97316]" /> :
                           ad.format === 'carousel' ? <Layers size={14} className="text-[#F97316]" /> :
                           <FileImage size={14} className="text-[#F97316]" />}
                          <Input 
                            value={ad.name}
                            onChange={(e) => updateAd(adSet.id, ad.id, 'name', e.target.value)}
                            className="bg-transparent border-none p-0 h-auto text-xs font-medium text-white w-24"
                          />
                        </div>
                        <Button size="icon" variant="ghost" className="h-5 w-5 text-[#E63946]" onClick={() => deleteAd(adSet.id, ad.id)}>
                          <Trash2 size={12} />
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-[9px] text-[#6B7280]">Format</Label>
                          <Select value={ad.format} onValueChange={(v) => updateAd(adSet.id, ad.id, 'format', v)}>
                            <SelectTrigger className="h-7 bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                              {formatOptions.map(f => (
                                <SelectItem key={f.id} value={f.id} className="text-white text-xs">{f.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-[9px] text-[#6B7280]">CTA</Label>
                          <Select value={ad.cta} onValueChange={(v) => updateAd(adSet.id, ad.id, 'cta', v)}>
                            <SelectTrigger className="h-7 bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                              {ctaOptions.map(c => (
                                <SelectItem key={c} value={c} className="text-white text-xs">{c}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="mt-2">
                        <Label className="text-[9px] text-[#6B7280]">Copywriting / Hook</Label>
                        <Textarea 
                          value={ad.copywriting || ''}
                          onChange={(e) => updateAd(adSet.id, ad.id, 'copywriting', e.target.value)}
                          placeholder="Ad copy atau hook utama..."
                          className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white text-xs min-h-[50px]"
                        />
                      </div>
                    </div>
                  ))}
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="w-full text-[#F97316] text-xs border border-dashed border-[#F97316]/30 hover:bg-[#F97316]/10"
                    onClick={() => addAd(adSet.id)}
                  >
                    <Plus size={12} className="mr-1" /> Add Ad
                  </Button>
                </div>
              )}
            </div>
          </div>
        ))}
        </div>
        
        {/* Add New Ad Set */}
        <Button 
          variant="outline" 
          className="h-32 border-2 border-dashed border-[#3B82F6]/50 text-[#3B82F6] hover:bg-[#3B82F6]/10 hover:border-[#3B82F6]"
          onClick={addAdSet}
        >
          <Plus size={20} className="mr-2" /> Add Ad Set
        </Button>
      </div>

      {/* Summary */}
      <div className="bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-xl p-4">
        <h4 className="text-sm font-bold text-white mb-3">Summary</h4>
        <div className="grid grid-cols-4 gap-4">
          <div className="text-center p-3 bg-[#10B981]/10 rounded-lg border border-[#10B981]/30">
            <p className="text-xs text-[#6B7280]">Campaign</p>
            <p className="text-2xl font-bold text-[#10B981]">1</p>
          </div>
          <div className="text-center p-3 bg-[#3B82F6]/10 rounded-lg border border-[#3B82F6]/30">
            <p className="text-xs text-[#6B7280]">Ad Sets</p>
            <p className="text-2xl font-bold text-[#3B82F6]">{(adstrategy?.adSets || []).length}</p>
          </div>
          <div className="text-center p-3 bg-[#F97316]/10 rounded-lg border border-[#F97316]/30">
            <p className="text-xs text-[#6B7280]">Total Ads</p>
            <p className="text-2xl font-bold text-[#F97316]">{totalAds}</p>
          </div>
          <div className="text-center p-3 bg-[#22C55E]/10 rounded-lg border border-[#22C55E]/30">
            <p className="text-xs text-[#6B7280]">Total Budget</p>
            <p className="text-2xl font-bold text-[#22C55E]">RM {totalBudget.toLocaleString()}</p>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-6 text-xs text-[#6B7280]">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-[#10B981]" />
          <span>Campaign Level</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-[#3B82F6]" />
          <span>Ad Set Level</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-[#F97316]" />
          <span>Ad Level</span>
        </div>
      </div>
    </div>
  );
}

// Workflow Builder - IMPROVED with proper drag-drop, diamond shape, and connection ports
function WorkflowBuilder({ workflow, onUpdate }) {
  const departments = [
    { key: 'marketing', label: 'Marketing', color: '#E63946', icon: Megaphone },
    { key: 'sales', label: 'Sales', color: '#3B82F6', icon: ShoppingBag },
    { key: 'operations', label: 'Operations', color: '#10B981', icon: Settings },
  ];
  const nodeTypes = [
    { type: 'start', label: 'Start/End', color: '#10B981', shape: 'oval' },
    { type: 'process', label: 'Process', color: '#3B82F6', shape: 'rect' },
    { type: 'decision', label: 'Decision', color: '#F59E0B', shape: 'diamond' },
    { type: 'io', label: 'Input/Output', color: '#8B5CF6', shape: 'parallelogram' },
    { type: 'yes', label: '✔', color: '#10B981', shape: 'circle' },
    { type: 'no', label: '✘', color: '#E63946', shape: 'circle' },
  ];

  const [activeDept, setActiveDept] = useState('marketing');
  const [selectedNode, setSelectedNode] = useState(null);
  const [draggedNode, setDraggedNode] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [hoveredNode, setHoveredNode] = useState(null);
  const [connectFromPort, setConnectFromPort] = useState(null); // { nodeId, side }
  const [connectorType, setConnectorType] = useState('straight'); // 'straight' or 'elbow'
  const [editMode, setEditMode] = useState(true); // When false, hides connection ports
  const [zoom, setZoom] = useState(100); // Zoom percentage
  const canvasRef = useRef(null);
  const canvasContainerRef = useRef(null);

  const deptWorkflow = workflow?.[activeDept] || { projects: [], activeProject: null };
  const activeProjectId = deptWorkflow.activeProject;
  const activeProject = deptWorkflow.projects?.find(p => p.id === activeProjectId) || deptWorkflow.projects?.[0];

  const addProject = () => {
    const updated = { ...workflow };
    const newProject = { id: `proj${Date.now()}`, name: 'New Project', nodes: [], connections: [] };
    updated[activeDept] = { projects: [...(updated[activeDept]?.projects || []), newProject], activeProject: newProject.id };
    onUpdate(updated);
  };

  const selectProject = (projectId) => {
    const updated = { ...workflow };
    updated[activeDept] = { ...updated[activeDept], activeProject: projectId };
    onUpdate(updated);
    setSelectedNode(null);
  };

  const deleteProject = (projectId, e) => {
    e.stopPropagation();
    const updated = { ...workflow };
    const projects = (updated[activeDept]?.projects || []).filter(p => p.id !== projectId);
    updated[activeDept] = { projects, activeProject: projects.length > 0 ? projects[0].id : null };
    onUpdate(updated);
  };

  const updateProjectName = (projectId, name, e) => {
    e.stopPropagation();
    const updated = { ...workflow };
    updated[activeDept].projects = updated[activeDept].projects.map(p => p.id === projectId ? { ...p, name } : p);
    onUpdate(updated);
  };

  const addNode = (type) => {
    if (!activeProject) return;
    const updated = { ...workflow };
    const newNode = { id: Date.now(), type, label: '', x: 50 + ((activeProject.nodes?.length || 0) % 4) * 160, y: 50 + Math.floor((activeProject.nodes?.length || 0) / 4) * 120 };
    updated[activeDept].projects = updated[activeDept].projects.map(p => p.id === activeProject.id ? { ...p, nodes: [...(p.nodes || []), newNode] } : p);
    onUpdate(updated);
  };

  const updateNode = useCallback((nodeId, field, value) => {
    if (!activeProject) return;
    const updated = { ...workflow };
    updated[activeDept].projects = updated[activeDept].projects.map(p =>
      p.id === activeProject.id ? { ...p, nodes: (p.nodes || []).map(n => n.id === nodeId ? { ...n, [field]: value } : n) } : p
    );
    onUpdate(updated);
  }, [workflow, activeDept, activeProject, onUpdate]);

  const deleteNode = (nodeId) => {
    if (!activeProject) return;
    const updated = { ...workflow };
    updated[activeDept].projects = updated[activeDept].projects.map(p =>
      p.id === activeProject.id ? { ...p, nodes: (p.nodes || []).filter(n => n.id !== nodeId), connections: (p.connections || []).filter(c => c.from !== nodeId && c.to !== nodeId) } : p
    );
    onUpdate(updated);
    setSelectedNode(null);
  };

  const addConnection = (fromId, toId, fromSide = 'right', toSide = 'left') => {
    if (!activeProject || fromId === toId) return;
    const exists = (activeProject.connections || []).some(c => c.from === fromId && c.to === toId);
    if (exists) return;
    const updated = { ...workflow };
    updated[activeDept].projects = updated[activeDept].projects.map(p =>
      p.id === activeProject.id ? { ...p, connections: [...(p.connections || []), { from: fromId, to: toId, fromSide, toSide, type: connectorType }] } : p
    );
    onUpdate(updated);
  };

  // Delete a specific connection
  const deleteConnection = (connIdx) => {
    if (!activeProject) return;
    const updated = { ...workflow };
    updated[activeDept].projects = updated[activeDept].projects.map(p =>
      p.id === activeProject.id ? { ...p, connections: (p.connections || []).filter((_, i) => i !== connIdx) } : p
    );
    onUpdate(updated);
  };

  const handleNodeMouseDown = (e, nodeId) => {
    // Check if clicking on input - don't start drag
    if (e.target.tagName === 'INPUT') return;
    if (connectFromPort) return; // Don't start drag in connect mode
    e.preventDefault();
    const node = activeProject?.nodes?.find(n => n.id === nodeId);
    if (!node || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    setDragOffset({ x: e.clientX - rect.left - node.x, y: e.clientY - rect.top - node.y });
    setDraggedNode(nodeId);
    setSelectedNode(nodeId);
  };

  const handlePortClick = (nodeId, side) => {
    if (!connectFromPort) {
      setConnectFromPort({ nodeId, side });
    } else {
      if (connectFromPort.nodeId !== nodeId) {
        addConnection(connectFromPort.nodeId, nodeId, connectFromPort.side, side);
      }
      setConnectFromPort(null);
    }
  };

  const handleCanvasMouseMove = useCallback((e) => {
    if (!draggedNode || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const scale = zoom / 100;
    // Calculate position accounting for zoom scale - allow dragging anywhere in the expanded canvas
    const newX = Math.max(0, (e.clientX - rect.left) / scale - dragOffset.x);
    const newY = Math.max(0, (e.clientY - rect.top) / scale - dragOffset.y);
    
    // Use requestAnimationFrame for smoother dragging
    requestAnimationFrame(() => {
      updateNode(draggedNode, 'x', newX);
      updateNode(draggedNode, 'y', newY);
    });
  }, [draggedNode, dragOffset, updateNode, zoom]);

  const handleCanvasMouseUp = () => setDraggedNode(null);
  
  const handleCanvasClick = (e) => {
    // Only deselect if clicking on canvas background, not on nodes/inputs
    if (e.target === canvasRef.current || e.target.classList.contains('canvas-bg')) {
      if (connectFromPort) setConnectFromPort(null);
      setSelectedNode(null);
    }
  };

  useEffect(() => {
    if (draggedNode) {
      document.addEventListener('mousemove', handleCanvasMouseMove);
      document.addEventListener('mouseup', handleCanvasMouseUp);
      return () => { document.removeEventListener('mousemove', handleCanvasMouseMove); document.removeEventListener('mouseup', handleCanvasMouseUp); };
    }
  }, [draggedNode, handleCanvasMouseMove]);

  const deptColor = departments.find(d => d.key === activeDept)?.color || '#E63946';

  // Zoom controls
  const zoomIn = () => setZoom(prev => Math.min(prev + 25, 200));
  const zoomOut = () => setZoom(prev => Math.max(prev - 25, 25));
  const fitToScreen = () => setZoom(100);

  // Calculate node dimensions based on text content
  const getNodeDimensions = (node) => {
    const typeConfig = nodeTypes.find(t => t.type === node.type);
    // Yes/No icons smaller (24x24), diamond 70x70, others 130x50
    const baseW = typeConfig?.shape === 'diamond' ? 70 : typeConfig?.shape === 'circle' ? 24 : 130;
    const baseH = typeConfig?.shape === 'diamond' ? 70 : typeConfig?.shape === 'circle' ? 24 : 50;
    
    // Auto-expand based on text length (only for non-circle, non-diamond)
    if (node.label && typeConfig?.shape !== 'diamond' && typeConfig?.shape !== 'circle') {
      const lines = node.label.split('\n');
      const maxLineLength = Math.max(...lines.map(l => l.length));
      const lineCount = lines.length;
      const calcW = Math.max(baseW, Math.min(maxLineLength * 8 + 20, 250));
      const calcH = Math.max(baseH, lineCount * 20 + 20);
      return { w: calcW, h: calcH };
    }
    return { w: baseW, h: baseH };
  };

  // Get node port positions (outside the shape)
  const getNodePortPosition = (node, side) => {
    const typeConfig = nodeTypes.find(t => t.type === node.type);
    const { w, h } = getNodeDimensions(node);
    const cx = node.x + w / 2;
    const cy = node.y + h / 2;
    
    // For diamond, adjust port positions due to rotation
    if (typeConfig?.shape === 'diamond') {
      const offset = 40; // Distance from center to edge
      switch(side) {
        case 'top': return { x: cx, y: cy - offset };
        case 'bottom': return { x: cx, y: cy + offset };
        case 'left': return { x: cx - offset, y: cy };
        case 'right': return { x: cx + offset, y: cy };
        default: return { x: cx, y: cy };
      }
    }
    
    // For circle (Yes/No icons) - smaller size
    if (typeConfig?.shape === 'circle') {
      switch(side) {
        case 'top': return { x: cx, y: node.y };
        case 'bottom': return { x: cx, y: node.y + h };
        case 'left': return { x: node.x, y: cy };
        case 'right': return { x: node.x + w, y: cy };
        default: return { x: cx, y: cy };
      }
    }
    
    switch(side) {
      case 'top': return { x: cx, y: node.y };
      case 'bottom': return { x: cx, y: node.y + h };
      case 'left': return { x: node.x, y: cy };
      case 'right': return { x: node.x + w, y: cy };
      default: return { x: cx, y: cy };
    }
  };

  // Generate path for connection arrows
  const getConnectionPath = (conn) => {
    const fromNode = (activeProject?.nodes || []).find(n => n.id === conn.from);
    const toNode = (activeProject?.nodes || []).find(n => n.id === conn.to);
    if (!fromNode || !toNode) return null;
    
    const fromSide = conn.fromSide || 'right';
    const toSide = conn.toSide || 'left';
    const from = getNodePortPosition(fromNode, fromSide);
    const to = getNodePortPosition(toNode, toSide);
    
    const type = conn.type || 'straight';
    
    if (type === 'elbow') {
      // Elbow connector with rounded corners
      const midX = (from.x + to.x) / 2;
      const radius = 10;
      
      if (fromSide === 'right' && toSide === 'left') {
        return `M ${from.x} ${from.y} L ${midX - radius} ${from.y} Q ${midX} ${from.y} ${midX} ${from.y + (to.y > from.y ? radius : -radius)} L ${midX} ${to.y - (to.y > from.y ? radius : -radius)} Q ${midX} ${to.y} ${midX + radius} ${to.y} L ${to.x} ${to.y}`;
      } else if (fromSide === 'bottom' && toSide === 'top') {
        const midY = (from.y + to.y) / 2;
        return `M ${from.x} ${from.y} L ${from.x} ${midY - radius} Q ${from.x} ${midY} ${from.x + (to.x > from.x ? radius : -radius)} ${midY} L ${to.x - (to.x > from.x ? radius : -radius)} ${midY} Q ${to.x} ${midY} ${to.x} ${midY + radius} L ${to.x} ${to.y}`;
      }
      // Default elbow
      return `M ${from.x} ${from.y} L ${from.x} ${to.y} L ${to.x} ${to.y}`;
    }
    
    // Straight line
    return `M ${from.x} ${from.y} L ${to.x} ${to.y}`;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        {departments.map(dept => (
          <button key={dept.key} onClick={() => { setActiveDept(dept.key); setSelectedNode(null); setConnectFromPort(null); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${activeDept === dept.key ? 'bg-[hsl(var(--surface))] ring-1' : 'bg-[hsl(var(--surface-elevated))]'}`}
            style={{ ringColor: activeDept === dept.key ? dept.color : undefined }}>
            <dept.icon size={16} style={{ color: dept.color }} />
            <span className={`text-sm ${activeDept === dept.key ? 'text-white font-semibold' : 'text-[#9CA3AF]'}`}>{dept.label}</span>
          </button>
        ))}
      </div>

      {/* Projects Bar */}
      <div className="flex items-center gap-2 p-3 bg-[hsl(var(--surface-elevated))] rounded-lg overflow-x-auto">
        <FolderOpen size={16} className="text-[#9CA3AF] flex-shrink-0" />
        {(deptWorkflow.projects || []).map(proj => (
          <div key={proj.id} onClick={() => selectProject(proj.id)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded cursor-pointer transition-all group ${proj.id === activeProjectId ? 'bg-[hsl(var(--surface))]' : 'hover:bg-[hsl(var(--surface))]/50'}`}>
            <span className={`text-sm ${proj.id === activeProjectId ? 'text-white font-medium' : 'text-[#9CA3AF]'}`}>{proj.name}</span>
            <button onClick={(e) => { e.stopPropagation(); const newName = prompt('Rename project:', proj.name); if (newName) updateProjectName(proj.id, newName, e); }}
              className="text-[#9CA3AF] hover:text-white"><Edit2 size={12} /></button>
            {(deptWorkflow.projects || []).length > 1 && (
              <button onClick={(e) => deleteProject(proj.id, e)} className="opacity-0 group-hover:opacity-100 text-[#E63946]/60 hover:text-[#E63946]"><X size={12} /></button>
            )}
          </div>
        ))}
        <Button size="sm" variant="ghost" className="h-7 px-3 text-sm text-[#9CA3AF]" onClick={addProject}>
          <Plus size={14} className="mr-1" />Project
        </Button>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-2 p-3 bg-[hsl(var(--surface-elevated))] rounded-lg flex-wrap">
        <span className="text-sm text-[#9CA3AF]">Add:</span>
        {nodeTypes.filter(nt => nt.type !== 'yes' && nt.type !== 'no').map(nt => (
          <Button key={nt.type} size="sm" variant="outline" className="h-8 text-sm border-[hsl(var(--border))]" onClick={() => addNode(nt.type)}>
            <span className={`w-4 h-3 mr-1.5`} style={{ 
              backgroundColor: nt.color, 
              borderRadius: nt.shape === 'oval' ? '50%' : nt.shape === 'diamond' ? '2px' : '3px',
              transform: nt.shape === 'parallelogram' ? 'skewX(-15deg)' : nt.shape === 'diamond' ? 'rotate(45deg) scale(0.7)' : undefined
            }} />
            {nt.label}
          </Button>
        ))}
        {/* Decision icons */}
        <div className="h-6 w-px bg-[hsl(var(--border))] mx-1" />
        <Button size="sm" variant="outline" className="h-7 px-2 text-xs border-[hsl(var(--border))] text-[#10B981]" onClick={() => addNode('yes')}>
          <Check size={12} />
        </Button>
        <Button size="sm" variant="outline" className="h-7 px-2 text-xs border-[hsl(var(--border))] text-[#E63946]" onClick={() => addNode('no')}>
          <X size={12} />
        </Button>
        
        <div className="flex-1" />
        
        {/* Zoom controls */}
        <div className="flex items-center gap-1 bg-[hsl(var(--surface))] rounded-lg p-1">
          <button onClick={zoomOut} className="px-2 py-1 rounded text-xs text-[#9CA3AF] hover:text-white hover:bg-[hsl(var(--surface-elevated))] transition-all" title="Zoom Out">
            <Minus size={14} />
          </button>
          <span className="px-2 text-xs text-white font-medium min-w-[45px] text-center">{zoom}%</span>
          <button onClick={zoomIn} className="px-2 py-1 rounded text-xs text-[#9CA3AF] hover:text-white hover:bg-[hsl(var(--surface-elevated))] transition-all" title="Zoom In">
            <Plus size={14} />
          </button>
          <button onClick={fitToScreen} className="px-2 py-1 rounded text-xs text-[#9CA3AF] hover:text-white hover:bg-[hsl(var(--surface-elevated))] transition-all ml-1" title="Fit to Screen">
            <Maximize2 size={14} />
          </button>
        </div>
        
        <div className="h-6 w-px bg-[hsl(var(--border))] mx-1" />
        
        {/* Connector type toggle */}
        <div className="flex items-center gap-1 bg-[hsl(var(--surface))] rounded-lg p-1">
          <button onClick={() => setConnectorType('straight')}
            className={`px-2 py-1 rounded text-xs transition-all ${connectorType === 'straight' ? 'bg-[hsl(var(--surface-elevated))] text-white' : 'text-[#9CA3AF]'}`}>
            ─
          </button>
          <button onClick={() => setConnectorType('elbow')}
            className={`px-2 py-1 rounded text-xs transition-all ${connectorType === 'elbow' ? 'bg-[hsl(var(--surface-elevated))] text-white' : 'text-[#9CA3AF]'}`}>
            └
          </button>
        </div>
        
        {/* Edit/Done toggle */}
        <Button size="sm" variant={editMode ? 'default' : 'outline'} 
          className={`h-8 text-sm ${editMode ? 'bg-[#10B981] hover:bg-[#059669]' : 'border-[hsl(var(--border))] text-[#9CA3AF]'}`}
          onClick={() => { setEditMode(!editMode); setSelectedNode(null); setConnectFromPort(null); }}>
          {editMode ? <><Check size={14} className="mr-1" />Done</> : <><Edit2 size={14} className="mr-1" />Edit</>}
        </Button>
      </div>

      {/* Canvas with Scroll and Zoom - Larger workspace */}
      <div ref={canvasContainerRef} className="relative rounded-xl border border-[hsl(var(--border))] overflow-auto" style={{ height: '450px' }}>
        <div ref={canvasRef} onClick={handleCanvasClick}
          className={`relative bg-[hsl(var(--surface))] focus:outline-none transition-none ${draggedNode ? 'cursor-grabbing' : ''}`}
          style={{ 
            width: '2400px',
            height: '1600px',
            transform: `scale(${zoom / 100})`,
            transformOrigin: 'top left',
            userSelect: draggedNode ? 'none' : 'auto'
          }}
          tabIndex={-1}>
          <div className="canvas-bg absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle, #6B7280 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
          
          {/* Connection arrows - clickable for deletion */}
          <svg className="absolute inset-0 w-full h-full" style={{ zIndex: 5 }}>
            <defs>
              <marker id={`arrowhead-${activeDept}`} markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                <polygon points="0 0, 10 3.5, 0 7" fill={deptColor} />
              </marker>
            </defs>
          {(activeProject?.connections || []).map((conn, idx) => {
            const pathD = getConnectionPath(conn);
            if (!pathD) return null;
            return (
              <g key={idx} className="group cursor-pointer" onClick={(e) => { e.stopPropagation(); }}>
                {/* Invisible wider path for easier clicking */}
                <path d={pathD} stroke="transparent" strokeWidth="15" fill="none" className="cursor-pointer"
                  onClick={() => editMode && deleteConnection(idx)} />
                {/* Visible arrow */}
                <path d={pathD} stroke={deptColor} strokeWidth="2" fill="none"
                  markerEnd={`url(#arrowhead-${activeDept})`} className="pointer-events-none" />
                {/* Delete button on hover - only in edit mode */}
                {editMode && (
                <foreignObject x={0} y={0} width="100%" height="100%" className="pointer-events-none">
                  <div className="w-full h-full relative">
                    {(() => {
                      const fromNode = (activeProject?.nodes || []).find(n => n.id === conn.from);
                      const toNode = (activeProject?.nodes || []).find(n => n.id === conn.to);
                      if (!fromNode || !toNode) return null;
                      const from = getNodePortPosition(fromNode, conn.fromSide || 'right');
                      const to = getNodePortPosition(toNode, conn.toSide || 'left');
                      const midX = (from.x + to.x) / 2;
                      const midY = (from.y + to.y) / 2;
                      return (
                        <button onClick={(e) => { e.stopPropagation(); deleteConnection(idx); }}
                          className="absolute w-5 h-5 rounded-full bg-[#E63946] text-white flex items-center justify-center shadow opacity-0 hover:opacity-100 pointer-events-auto transition-opacity"
                          style={{ left: midX - 10, top: midY - 10 }}>
                          <X size={12} />
                        </button>
                      );
                    })()}
                  </div>
                </foreignObject>
                )}
              </g>
            );
          })}
        </svg>

        {/* Nodes with connection ports */}
        {(activeProject?.nodes || []).map(node => {
          const typeConfig = nodeTypes.find(t => t.type === node.type);
          const isSelected = selectedNode === node.id;
          const isDragging = draggedNode === node.id;
          const isHovered = hoveredNode === node.id;
          const showPorts = editMode && (isHovered || isSelected || connectFromPort);
          
          // Get dynamic dimensions based on text
          const { w: nodeW, h: nodeH } = getNodeDimensions(node);
          
          // Different shapes
          let shapeStyle = {};
          let shapeClass = 'rounded-lg';
          
          if (typeConfig?.shape === 'oval') {
            shapeClass = 'rounded-full';
            shapeStyle = { borderRadius: '25px' };
          } else if (typeConfig?.shape === 'diamond') {
            shapeClass = '';
            shapeStyle = { transform: 'rotate(45deg)', borderRadius: '4px' };
          } else if (typeConfig?.shape === 'parallelogram') {
            shapeClass = 'rounded';
            shapeStyle = { transform: 'skewX(-10deg)' };
          } else if (typeConfig?.shape === 'circle') {
            shapeClass = 'rounded-full';
          }
          
          // Connection port positions - dynamic based on node size
          const portPositions = typeConfig?.shape === 'diamond' 
            ? { top: { x: nodeW/2, y: -5 }, bottom: { x: nodeW/2, y: nodeH+5 }, left: { x: -5, y: nodeH/2 }, right: { x: nodeW+5, y: nodeH/2 } }
            : { top: { x: nodeW/2, y: -5 }, bottom: { x: nodeW/2, y: nodeH+5 }, left: { x: -5, y: nodeH/2 }, right: { x: nodeW+5, y: nodeH/2 } };
          
          // For Yes/No icons, show the icon instead of input
          const isDecisionIcon = typeConfig?.type === 'yes' || typeConfig?.type === 'no';
          
          return (
            <div key={node.id} 
              onMouseDown={(e) => handleNodeMouseDown(e, node.id)}
              onMouseEnter={() => setHoveredNode(node.id)}
              onMouseLeave={() => setHoveredNode(null)}
              className={`absolute transition-shadow ${isDragging ? 'cursor-grabbing z-30' : 'cursor-grab z-10'} ${isSelected ? 'z-20' : ''}`}
              style={{ left: node.x, top: node.y }}>
              
              {/* Connection ports - 4 sides - only in edit mode */}
              {showPorts && ['top', 'bottom', 'left', 'right'].map(side => (
                <button key={side}
                  onClick={(e) => { e.stopPropagation(); handlePortClick(node.id, side); }}
                  className={`absolute w-3 h-3 rounded-full border-2 transition-all z-50 ${
                    connectFromPort?.nodeId === node.id && connectFromPort?.side === side 
                      ? 'bg-[#8B5CF6] border-[#8B5CF6] scale-125' 
                      : 'bg-white border-[#6B7280] hover:border-[#8B5CF6] hover:bg-[#8B5CF6]/20'
                  }`}
                  style={{ 
                    left: portPositions[side].x - 6, 
                    top: portPositions[side].y - 6,
                    cursor: 'crosshair'
                  }}
                />
              ))}
              
              <div className={`relative flex items-center justify-center transition-all ${shapeClass}`}
                style={{ 
                  ...shapeStyle,
                  width: `${nodeW}px`,
                  height: `${nodeH}px`,
                  minWidth: typeConfig?.shape === 'circle' ? '24px' : undefined,
                  minHeight: typeConfig?.shape === 'circle' ? '24px' : undefined,
                  backgroundColor: `${typeConfig?.color}30`,
                  border: `2px solid ${typeConfig?.color}`,
                  boxShadow: isSelected ? `0 0 0 3px ${typeConfig?.color}50` : undefined
                }}>
                {/* Delete button - only in edit mode */}
                {editMode && isSelected && (
                  <button onClick={(e) => { e.stopPropagation(); deleteNode(node.id); }}
                    className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-[#E63946] text-white flex items-center justify-center shadow z-40"
                    style={{ transform: typeConfig?.shape === 'diamond' ? 'rotate(-45deg)' : undefined }}>
                    <X size={12} />
                  </button>
                )}
                
                {/* For Yes/No icons, show icon instead of input */}
                {isDecisionIcon ? (
                  <div className="flex items-center justify-center" 
                    style={{ transform: typeConfig?.shape === 'diamond' ? 'rotate(-45deg)' : undefined }}>
                    {typeConfig?.type === 'yes' ? <Check size={14} className="text-[#10B981]" /> : <X size={14} className="text-[#E63946]" />}
                  </div>
                ) : (
                  <textarea 
                    value={node.label || ''} 
                    onChange={(e) => { e.stopPropagation(); updateNode(node.id, 'label', e.target.value); }}
                    onClick={(e) => { e.stopPropagation(); setSelectedNode(node.id); }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    placeholder={typeConfig?.label}
                    rows={1}
                    className="w-full bg-transparent text-white text-center text-xs font-medium px-2 outline-none border-none resize-none overflow-hidden flex items-center justify-center"
                    style={{ 
                      transform: typeConfig?.shape === 'diamond' ? 'rotate(-45deg)' : typeConfig?.shape === 'parallelogram' ? 'skewX(10deg)' : undefined,
                      caretColor: 'white',
                      lineHeight: '1.4',
                      paddingTop: typeConfig?.shape === 'diamond' ? '0' : `${Math.max(4, (nodeH - 20) / 2)}px`,
                      height: typeConfig?.shape === 'diamond' ? '50px' : '100%'
                    }} />
                )}
              </div>
            </div>
          );
        })}

        {(!activeProject || (activeProject?.nodes || []).length === 0) && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <Workflow size={40} className="mx-auto mb-3 text-[#6B7280]/50" />
              <p className="text-sm text-[#9CA3AF]">Tiada workflow lagi</p>
              <p className="text-xs text-[#6B7280]">Klik butang di atas untuk tambah node</p>
            </div>
          </div>
        )}
        </div>
      </div>
      {editMode && connectFromPort && (
        <p className="text-sm text-[#8B5CF6] text-center mt-2">💡 Klik pada dot di node lain untuk connect dengan arrow</p>
      )}
      {editMode && !connectFromPort && (activeProject?.nodes || []).length > 0 && (
        <p className="text-xs text-[#6B7280] text-center mt-2">💡 Gunakan 🔍+/- untuk zoom. Scroll untuk navigate. Tekan "Done" bila selesai.</p>
      )}
      {!editMode && (activeProject?.nodes || []).length > 0 && (
        <p className="text-xs text-[#10B981] text-center mt-2">✓ Workflow siap. Tekan "Edit" untuk edit semula.</p>
      )}
    </div>
  );
}

// Budget - Premium Design with Daily Breakdown (Uses Phase Timeline dates)
function BudgetByPhaseAndPlatform({ budget, onUpdate, phasesTimeline }) {
  const allPhases = [
    { key: 'prospecting', label: 'Prospecting', color: '#8B5CF6', icon: Users },
    { key: 'seeding', label: 'Seeding', color: '#06B6D4', icon: Sparkles },
    { key: 'buzz', label: 'Buzz', color: '#EC4899', icon: Zap },
    { key: 'launching', label: 'Launching', color: '#F97316', icon: Rocket },
    { key: 'closing', label: 'Closing', color: '#14B8A6', icon: Target },
  ];
  
  // Get enabled phases from budget data or initialize with default
  const [enabledPhases, setEnabledPhases] = useState(() => {
    if (budget.enabledPhases?.length > 0) return budget.enabledPhases;
    const enabled = ['seeding', 'buzz', 'launching'];
    if (budget.byPhase?.prospecting?.length > 0) enabled.unshift('prospecting');
    if (budget.byPhase?.closing?.length > 0) enabled.push('closing');
    return enabled;
  });
  
  const [activePhase, setActivePhase] = useState(() => enabledPhases[0] || 'seeding');
  
  // Calculate days from Phase Timeline dates
  const getPhaseDaysFromTimeline = (phaseKey) => {
    const phaseMap = {
      'prospecting': 'prospecting',
      'seeding': 'seeding', 
      'buzz': 'buzz',
      'launching': 'launching',
      'closing': 'closing'
    };
    
    const phaseData = (phasesTimeline || []).find(p => p.id === phaseMap[phaseKey] || p.label?.toLowerCase() === phaseKey);
    
    if (phaseData?.startDate && phaseData?.endDate) {
      const start = new Date(phaseData.startDate);
      const end = new Date(phaseData.endDate);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      return { days: diffDays, startDate: phaseData.startDate, endDate: phaseData.endDate, fromTimeline: true };
    }
    
    return { days: 0, startDate: null, endDate: null, fromTimeline: false };
  };
  
  // Add phase
  const addPhase = (phaseKey) => {
    const allKeys = allPhases.map(p => p.key);
    const newEnabled = [...enabledPhases, phaseKey].sort((a, b) => allKeys.indexOf(a) - allKeys.indexOf(b));
    setEnabledPhases(newEnabled);
    
    // Initialize budget data for new phase
    const updated = { 
      ...budget, 
      enabledPhases: newEnabled,
      byPhase: { 
        ...budget.byPhase, 
        [phaseKey]: budget.byPhase?.[phaseKey] || socialPlatforms.map(p => ({ platform: p.id, allocated: 0 })) 
      } 
    };
    onUpdate(updated);
  };
  
  // Delete phase - ANY phase can be deleted now
  const deletePhase = (phaseKey) => {
    if (enabledPhases.length <= 1) return; // Keep at least one phase
    
    const newEnabled = enabledPhases.filter(p => p !== phaseKey);
    setEnabledPhases(newEnabled);
    
    // Update active phase if deleted
    if (activePhase === phaseKey) {
      setActivePhase(newEnabled[0]);
    }
    
    // Remove budget data for deleted phase and recalculate total
    const updated = { ...budget, enabledPhases: newEnabled };
    const removedBudget = (budget.byPhase?.[phaseKey] || []).reduce((sum, p) => sum + (p.allocated || 0), 0);
    updated.totalBudget = (budget.totalBudget || 0) - removedBudget;
    
    // Remove phase from byPhase
    const { [phaseKey]: removed, ...remainingPhases } = budget.byPhase || {};
    updated.byPhase = remainingPhases;
    
    onUpdate(updated);
  };
  
  const updatePlatformBudget = (phaseKey, platformId, value) => {
    const updated = { ...budget, byPhase: { ...budget.byPhase }, enabledPhases };
    updated.byPhase[phaseKey] = (updated.byPhase[phaseKey] || []).map(p => p.platform === platformId ? { ...p, allocated: parseFloat(value) || 0 } : p);
    let total = 0; 
    enabledPhases.forEach(phase => {
      (updated.byPhase[phase] || []).forEach(p => { total += p.allocated || 0; });
    });
    updated.totalBudget = total;
    onUpdate(updated);
  };
  
  const addPlatform = (phaseKey) => { 
    const updated = { ...budget, byPhase: { ...budget.byPhase }, enabledPhases }; 
    updated.byPhase[phaseKey] = [...(updated.byPhase[phaseKey] || []), { platform: 'custom', allocated: 0, customName: '' }]; 
    onUpdate(updated); 
  };
  
  const deletePlatform = (phaseKey, idx) => { 
    const updated = { ...budget, byPhase: { ...budget.byPhase }, enabledPhases }; 
    const removed = updated.byPhase[phaseKey][idx]; 
    updated.byPhase[phaseKey] = (updated.byPhase[phaseKey] || []).filter((_, i) => i !== idx); 
    updated.totalBudget = (updated.totalBudget || 0) - (removed?.allocated || 0); 
    onUpdate(updated); 
  };
  
  const getPhaseTotal = (phaseKey) => (budget.byPhase?.[phaseKey] || []).reduce((sum, p) => sum + (p.allocated || 0), 0);
  
  const getPhaseDaily = (phaseKey) => {
    const phaseTotal = getPhaseTotal(phaseKey);
    const { days } = getPhaseDaysFromTimeline(phaseKey);
    return days > 0 ? phaseTotal / days : 0;
  };
  
  // Calculate total campaign days from all enabled phases' timeline
  const getTotalCampaignDays = () => {
    let totalDays = 0;
    enabledPhases.forEach(phase => {
      const { days } = getPhaseDaysFromTimeline(phase);
      totalDays += days;
    });
    return totalDays;
  };
  
  const getOverallDailySpend = () => {
    const totalDays = getTotalCampaignDays();
    return totalDays > 0 ? (budget.totalBudget || 0) / totalDays : 0;
  };
  
  const visiblePhases = allPhases.filter(p => enabledPhases.includes(p.key));
  const availablePhases = allPhases.filter(p => !enabledPhases.includes(p.key));

  return (
    <div className="space-y-6">
      {/* Premium Header Card */}
      <div className="relative overflow-hidden rounded-2xl border border-[hsl(var(--border))]">
        <div className="absolute inset-0 bg-gradient-to-br from-[#22C55E]/10 via-[#10B981]/5 to-[#3B82F6]/10" />
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#22C55E]/10 rounded-full blur-3xl -translate-y-32 translate-x-32" />
        
        <div className="relative p-6">
          <div className="grid grid-cols-4 gap-6">
            {/* Total Budget */}
            <div className="col-span-1">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#22C55E] to-[#10B981] flex items-center justify-center shadow-lg shadow-[#22C55E]/20">
                  <DollarSign size={24} className="text-white" />
                </div>
                <div>
                  <p className="text-xs text-[#6B7280] uppercase tracking-wider">Total Budget</p>
                  <p className="text-3xl font-bold text-white">RM {(budget.totalBudget || 0).toLocaleString()}</p>
                </div>
              </div>
            </div>
            
            {/* Campaign Duration - Auto from Timeline */}
            <div className="col-span-1">
              <div className="p-4 rounded-xl bg-[hsl(var(--surface))]/50 border border-[hsl(var(--border))]">
                <p className="text-xs text-[#6B7280] uppercase tracking-wider mb-1">Tempoh Campaign</p>
                <p className="text-2xl font-bold text-white">
                  {getTotalCampaignDays() || <span className="text-[#6B7280] text-lg">Set di Phase Timeline</span>}
                  {getTotalCampaignDays() > 0 && <span className="text-sm font-normal text-[#9CA3AF] ml-1">hari</span>}
                </p>
                <p className="text-[10px] text-[#6B7280]">Auto dari Phase Timeline</p>
              </div>
            </div>
            
            {/* Overall Daily Spend */}
            <div className="col-span-1">
              <div className="p-4 rounded-xl bg-gradient-to-br from-[#F59E0B]/20 to-[#F97316]/10 border border-[#F59E0B]/30">
                <p className="text-xs text-[#F59E0B] uppercase tracking-wider mb-1">Daily Spend (Overall)</p>
                <p className="text-2xl font-bold text-[#F59E0B]">
                  RM {getOverallDailySpend().toFixed(2)}
                </p>
                <p className="text-[10px] text-[#9CA3AF]">per hari keseluruhan</p>
              </div>
            </div>
            
            {/* Active Phase Daily */}
            <div className="col-span-1">
              {(() => {
                const phaseInfo = getPhaseDaysFromTimeline(activePhase);
                const phaseConfig = allPhases.find(p => p.key === activePhase);
                return (
                  <div className="p-4 rounded-xl border-2" style={{ 
                    backgroundColor: `${phaseConfig?.color}15`,
                    borderColor: phaseConfig?.color 
                  }}>
                    <p className="text-xs uppercase tracking-wider mb-1" style={{ color: phaseConfig?.color }}>
                      Daily ({phaseConfig?.label})
                    </p>
                    <p className="text-2xl font-bold" style={{ color: phaseConfig?.color }}>
                      RM {getPhaseDaily(activePhase).toFixed(2)}
                    </p>
                    <p className="text-[10px] text-[#9CA3AF]">
                      {phaseInfo.fromTimeline 
                        ? `${phaseInfo.days} hari (${phaseInfo.startDate} - ${phaseInfo.endDate})`
                        : 'Tarikh belum di-set'
                      }
                    </p>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* Phase Tabs with Delete */}
      <div className="flex items-center gap-2 flex-wrap">
        {visiblePhases.map((phase) => {
          const PhaseIcon = phase.icon;
          const isActive = activePhase === phase.key;
          const phaseTotal = getPhaseTotal(phase.key);
          const phaseDaily = getPhaseDaily(phase.key);
          const phaseInfo = getPhaseDaysFromTimeline(phase.key);
          
          return (
            <div
              key={phase.key}
              className={`relative flex items-center gap-3 px-4 py-3 rounded-xl transition-all group ${
                isActive 
                  ? 'shadow-lg' 
                  : 'bg-[hsl(var(--surface-elevated))] hover:bg-[hsl(var(--surface))]'
              }`}
              style={isActive ? { 
                backgroundColor: `${phase.color}20`,
                boxShadow: `0 0 30px -5px ${phase.color}40`,
                border: `2px solid ${phase.color}`
              } : { border: '2px solid transparent' }}
            >
              <button
                onClick={() => setActivePhase(phase.key)}
                className="flex items-center gap-3"
              >
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${phase.color}30` }}>
                  <PhaseIcon size={16} style={{ color: phase.color }} />
                </div>
                <div className="text-left">
                  <p className="text-sm font-bold" style={{ color: isActive ? phase.color : '#fff' }}>{phase.label}</p>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[#9CA3AF]">RM {phaseTotal.toLocaleString()}</span>
                    {phaseInfo.fromTimeline ? (
                      <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ backgroundColor: `${phase.color}20`, color: phase.color }}>
                        {phaseInfo.days}d • RM {phaseDaily.toFixed(0)}/day
                      </span>
                    ) : (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#E63946]/20 text-[#E63946]">
                        No dates
                      </span>
                    )}
                  </div>
                </div>
              </button>
              
              {/* Delete button - visible on hover */}
              {enabledPhases.length > 1 && (
                <Button 
                  size="icon" 
                  variant="ghost" 
                  className="h-6 w-6 opacity-0 group-hover:opacity-100 text-[#E63946] hover:bg-[#E63946]/20 absolute -top-2 -right-2 rounded-full bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))]"
                  onClick={(e) => { e.stopPropagation(); deletePhase(phase.key); }}
                >
                  <X size={12} />
                </Button>
              )}
            </div>
          );
        })}
        
        {/* Add Phase Dropdown */}
        {availablePhases.length > 0 && (
          <div className="relative group">
            <Button 
              variant="outline" 
              className="border-dashed border-[#10B981]/50 text-[#10B981] hover:bg-[#10B981]/10"
            >
              <Plus size={14} className="mr-1" /> Tambah Fasa
            </Button>
            <div className="absolute top-full left-0 mt-2 bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10 min-w-[150px]">
              {availablePhases.map(phase => {
                const PhaseIcon = phase.icon;
                return (
                  <button
                    key={phase.key}
                    onClick={() => addPhase(phase.key)}
                    className="w-full flex items-center gap-2 px-3 py-2 hover:bg-[hsl(var(--surface))] text-left transition-colors"
                  >
                    <PhaseIcon size={14} style={{ color: phase.color }} />
                    <span className="text-sm text-white">{phase.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Active Phase Budget Breakdown */}
      <div className="rounded-2xl border-2 overflow-hidden" style={{ borderColor: allPhases.find(p => p.key === activePhase)?.color }}>
        <div className="p-4 flex items-center justify-between" style={{ backgroundColor: `${allPhases.find(p => p.key === activePhase)?.color}15` }}>
          <div className="flex items-center gap-3">
            {React.createElement(allPhases.find(p => p.key === activePhase)?.icon || Target, { size: 20, style: { color: allPhases.find(p => p.key === activePhase)?.color }})}
            <div>
              <p className="font-bold text-white">{allPhases.find(p => p.key === activePhase)?.label} Budget Breakdown</p>
              <p className="text-xs text-[#6B7280]">Budget per platform untuk fasa ini</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold" style={{ color: allPhases.find(p => p.key === activePhase)?.color }}>
              RM {getPhaseTotal(activePhase).toLocaleString()}
            </p>
            {(() => {
              const info = getPhaseDaysFromTimeline(activePhase);
              return info.fromTimeline ? (
                <p className="text-xs text-[#6B7280]">{info.days} hari • RM {getPhaseDaily(activePhase).toFixed(2)}/day</p>
              ) : (
                <p className="text-xs text-[#E63946]">Set tarikh di Phase Timeline</p>
              );
            })()}
          </div>
        </div>
        
        <div className="p-4 bg-[hsl(var(--surface))]">
          <div className="grid grid-cols-2 gap-3">
            {(budget.byPhase?.[activePhase] || socialPlatforms.map(p => ({ platform: p.id, allocated: 0 }))).map((item, idx) => {
              const platform = socialPlatforms.find(p => p.id === item.platform);
              const phaseInfo = getPhaseDaysFromTimeline(activePhase);
              const dailyPlatform = phaseInfo.days > 0 ? (item.allocated || 0) / phaseInfo.days : 0;
              
              return (
                <div 
                  key={idx} 
                  className="flex items-center gap-3 p-3 rounded-xl bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] hover:border-[hsl(var(--border-hover))] transition-all group"
                >
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${platform?.color || '#6B7280'}20` }}>
                    {platform ? React.createElement(platform.icon, { size: 18, style: { color: platform.color } }) : <Globe2 size={18} className="text-[#6B7280]" />}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-white">{platform?.label || 'Custom'}</p>
                    <p className="text-[10px] text-[#6B7280]">RM {dailyPlatform.toFixed(2)}/day</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[#9CA3AF]">RM</span>
                    <Input 
                      type="number"
                      value={item.allocated || ''}
                      onChange={(e) => updatePlatformBudget(activePhase, item.platform, e.target.value)}
                      placeholder="0"
                      className="w-24 h-9 bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white font-bold text-right"
                    />
                  </div>
                  <Button 
                    size="icon" 
                    variant="ghost" 
                    className="h-8 w-8 opacity-0 group-hover:opacity-100 text-[#E63946]"
                    onClick={() => deletePlatform(activePhase, idx)}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              );
            })}
          </div>
          
          <Button 
            variant="outline" 
            className="w-full mt-3 border-dashed border-[hsl(var(--border))] text-[#9CA3AF]"
            onClick={() => addPlatform(activePhase)}
          >
            <Plus size={14} className="mr-1" /> Tambah Platform
          </Button>
        </div>
      </div>

      {/* Daily Breakdown Summary Table */}
      <div className="rounded-xl border border-[hsl(var(--border))] overflow-hidden">
        <div className="p-3 bg-gradient-to-r from-[#F59E0B]/10 to-transparent border-b border-[hsl(var(--border))]">
          <p className="text-sm font-bold text-white flex items-center gap-2">
            <Calendar size={14} className="text-[#F59E0B]" />
            Daily Spend Breakdown (By Phase & Platform)
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[hsl(var(--surface-elevated))]">
                <th className="p-3 text-left text-xs text-[#6B7280] font-medium">Platform</th>
                {visiblePhases.map(phase => {
                  const phaseInfo = getPhaseDaysFromTimeline(phase.key);
                  return (
                    <th key={phase.key} className="p-3 text-center text-xs font-medium" style={{ color: phase.color }}>
                      {phase.label}
                      <br />
                      {phaseInfo.fromTimeline ? (
                        <span className="text-[10px] text-[#6B7280]">{phaseInfo.days} days</span>
                      ) : (
                        <span className="text-[10px] text-[#E63946]">No dates</span>
                      )}
                    </th>
                  );
                })}
                <th className="p-3 text-center text-xs text-[#F59E0B] font-medium">Total Daily</th>
              </tr>
            </thead>
            <tbody>
              {socialPlatforms.map(platform => {
                let totalDaily = 0;
                return (
                  <tr key={platform.id} className="border-t border-[hsl(var(--border))] hover:bg-[hsl(var(--surface-elevated))]">
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        {React.createElement(platform.icon, { size: 14, style: { color: platform.color }})}
                        <span className="text-white text-xs">{platform.label}</span>
                      </div>
                    </td>
                    {visiblePhases.map(phase => {
                      const phaseData = budget.byPhase?.[phase.key] || [];
                      const platformBudget = phaseData.find(p => p.platform === platform.id)?.allocated || 0;
                      const phaseInfo = getPhaseDaysFromTimeline(phase.key);
                      const daily = phaseInfo.days > 0 ? platformBudget / phaseInfo.days : 0;
                      totalDaily += daily;
                      return (
                        <td key={phase.key} className="p-3 text-center">
                          <span className="font-mono text-xs" style={{ color: daily > 0 ? phase.color : '#6B7280' }}>
                            RM {daily.toFixed(2)}
                          </span>
                        </td>
                      );
                    })}
                    <td className="p-3 text-center">
                      <span className="font-mono text-xs font-bold text-[#F59E0B]">
                        RM {totalDaily.toFixed(2)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Document View - Campaign Overview (removed sub-campaign)
function DocumentView({ data }) {
  const totalMaterials = Object.values(data.materials || {}).flat();
  const materialsDone = totalMaterials.filter(m => m.status === 'done').length;
  const phasesWithDates = (data.phases?.timeline || []).filter(p => p.startDate && p.endDate);
  const currencySymbol = data.offer?.currency === 'USD' ? '$' : 'RM';
  
  return (
    <div className="space-y-6 p-6">
      <div className="bg-gradient-to-br from-[#E63946]/20 via-[#F97316]/10 to-transparent border border-[#E63946]/30 rounded-2xl p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <Badge className="mb-2" style={{ backgroundColor: `${campaignTypes[data.basic?.type]?.color}20`, color: campaignTypes[data.basic?.type]?.color }}>
              {campaignTypes[data.basic?.type]?.label}
            </Badge>
            <h2 className="text-2xl font-bold text-white">{data.basic?.name || 'Untitled Campaign'}</h2>
            <p className="text-sm text-[#9CA3AF] mt-1">{data.basic?.description || 'No description'}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-[#6B7280]">Total Budget</p>
            <p className="text-2xl font-bold text-white">RM {(data.budget?.totalBudget || 0).toLocaleString()}</p>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-[hsl(var(--surface))]/50 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-[#E63946]">{(data.objective?.kpis || []).filter(k => k.target).length}</p>
            <p className="text-[10px] text-[#9CA3AF]">KPIs Set</p>
          </div>
          <div className="bg-[hsl(var(--surface))]/50 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-[#10B981]">{materialsDone}/{totalMaterials.length}</p>
            <p className="text-[10px] text-[#9CA3AF]">Materials Ready</p>
          </div>
          <div className="bg-[hsl(var(--surface))]/50 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-[#8B5CF6]">{(data.team?.members || []).filter(m => m.name).length}</p>
            <p className="text-[10px] text-[#9CA3AF]">Team Assigned</p>
          </div>
          <div className="bg-[hsl(var(--surface))]/50 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-[#F59E0B]">{phasesWithDates.length}/8</p>
            <p className="text-[10px] text-[#9CA3AF]">Phases Planned</p>
          </div>
        </div>
      </div>
      
      <div className="bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-xl p-4">
        <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2"><Target size={16} className="text-[#F97316]" />Objective & KPIs</h3>
        <p className="text-sm text-[#9CA3AF] mb-4 p-3 bg-[hsl(var(--surface))] rounded-lg">{data.objective?.mainGoal || 'Not set'}</p>
        <div className="grid grid-cols-3 gap-3">
          {(data.objective?.kpis || []).filter(k => k.metric && k.target).map((kpi, idx) => (
            <div key={idx} className="bg-[hsl(var(--surface))] rounded-lg p-3 text-center">
              <p className="text-xs text-[#6B7280]">{kpi.metric}</p>
              <p className="text-xl font-bold text-white">{kpi.target || '-'}</p>
            </div>
          ))}
        </div>
      </div>

      {(data.offer?.packages || []).length > 0 && (
        <div className="bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-xl p-4">
          <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2"><Package size={16} className="text-[#F59E0B]" />Packages & Offers</h3>
          <div className="grid grid-cols-2 gap-4">
            {(data.offer?.packages || []).map((pkg, idx) => (
              <div key={idx} className="p-4 bg-[hsl(var(--surface))] rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-semibold text-white">{pkg.name}</p>
                  {pkg.price && <Badge className="bg-[#F59E0B]/20 text-[#F59E0B]">{currencySymbol} {pkg.price}</Badge>}
                </div>
                {pkg.urgency && <p className="text-xs text-[#E63946] mb-2">🔥 {pkg.urgency}</p>}
                <div className="space-y-1">
                  {(pkg.components || []).filter(c => c.included).map((comp, cIdx) => (
                    <p key={cIdx} className="text-xs text-[#9CA3AF] flex items-center gap-1"><Check size={10} className="text-[#10B981]" /> {comp.item}</p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      <div className="bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-xl p-4">
        <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2"><Calendar size={16} className="text-[#EC4899]" />Phase Timeline</h3>
        <div className="space-y-2">
          {(data.phases?.timeline || []).filter(p => p.status !== 'skipped').map((phase) => {
            const config = campaignPhases.find(p => p.id === phase.id);
            const hasDate = phase.startDate && phase.endDate;
            return (
              <div key={phase.id} className="flex items-center gap-3 p-2 rounded-lg" style={{ backgroundColor: hasDate ? `${config?.color}10` : 'transparent' }}>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${config?.color}20` }}>
                  {React.createElement(config?.icon || Circle, { size: 14, style: { color: config?.color } })}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-white">{phase.label}</p>
                  <p className="text-[10px] text-[#6B7280]">
                    {hasDate ? `${new Date(phase.startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - ${new Date(phase.endDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` : 'No dates set'}
                  </p>
                </div>
                <Badge className={`text-[9px] ${phase.status === 'completed' ? 'bg-[#10B981]/20 text-[#10B981]' : phase.status === 'running' ? 'bg-[#F59E0B]/20 text-[#F59E0B]' : 'bg-[#6B7280]/20 text-[#6B7280]'}`}>
                  {phase.status === 'completed' ? '✓ Done' : phase.status === 'running' ? '▶ Running' : '○ Pending'}
                </Badge>
              </div>
            );
          })}
        </div>
      </div>
      
      <div className="bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-xl p-4">
        <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2"><UserCheck size={16} className="text-[#3B82F6]" />Team Members</h3>
        <div className="grid grid-cols-2 gap-2">
          {(data.team?.members || []).filter(m => m.name || m.role).map((member, idx) => (
            <div key={idx} className="flex items-center gap-3 p-2 bg-[hsl(var(--surface))] rounded-lg">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#3B82F6] to-[#8B5CF6] flex items-center justify-center text-white text-xs font-bold">
                {member.name ? member.name.charAt(0).toUpperCase() : '?'}
              </div>
              <div><p className="text-sm text-white">{member.name || 'Unassigned'}</p><p className="text-[10px] text-[#6B7280]">{member.role}</p></div>
            </div>
          ))}
        </div>
      </div>
      
      <div className="bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-xl p-4">
        <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2"><DollarSign size={16} className="text-[#22C55E]" />Budget by Phase</h3>
        <div className="space-y-2">
          {['seeding', 'buzz', 'launching'].map(phaseKey => {
            const phaseTotal = (data.budget?.byPhase?.[phaseKey] || []).reduce((sum, p) => sum + (p.allocated || 0), 0);
            const phaseConfig = campaignPhases.find(p => p.id === phaseKey);
            return (
              <div key={phaseKey} className="flex items-center justify-between p-2 bg-[hsl(var(--surface))] rounded-lg">
                <span className="text-sm text-white capitalize flex items-center gap-2"><span style={{ color: phaseConfig?.color }}>●</span>{phaseKey}</span>
                <span className="text-sm font-bold text-[#22C55E]">RM {phaseTotal.toLocaleString()}</span>
              </div>
            );
          })}
          <div className="flex items-center justify-between p-3 bg-gradient-to-r from-[#22C55E]/20 to-[#10B981]/20 rounded-lg border border-[#22C55E]/30">
            <span className="text-sm font-bold text-white">Total Budget</span>
            <span className="text-lg font-bold text-[#22C55E]">RM {(data.budget?.totalBudget || 0).toLocaleString()}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Main Component
export function CampaignBlueprintWizard({ open, onOpenChange, campaign, onSave, mode = 'create', defaultViewMode = 'edit' }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [viewMode, setViewMode] = useState(defaultViewMode);
  
  useEffect(() => { if (open) setViewMode(defaultViewMode); }, [open, defaultViewMode]);
  
  const initialData = campaign?.blueprint ? { ...getDefaultBlueprintData(), ...campaign.blueprint } : getDefaultBlueprintData();
  const [blueprintData, setBlueprintData] = useState(initialData);
  
  const campaignId = campaign?.id;
  const [prevCampaignId, setPrevCampaignId] = useState(campaignId);
  const [prevOpen, setPrevOpen] = useState(open);
  
  // Reset when campaign changes OR when modal opens fresh for new campaign
  if (campaignId !== prevCampaignId || (open && !prevOpen)) {
    setPrevCampaignId(campaignId);
    setPrevOpen(open);
    setBlueprintData(campaign?.blueprint ? { ...getDefaultBlueprintData(), ...campaign.blueprint } : getDefaultBlueprintData());
    setCurrentStep(1);
    setViewMode(defaultViewMode);
  }
  if (open !== prevOpen) setPrevOpen(open);
  
  const updateField = (section, field, value) => setBlueprintData(prev => ({ ...prev, [section]: { ...prev[section], [field]: value } }));
  const updateMaterial = (dept, index, field, value) => {
    setBlueprintData(prev => {
      const newMaterials = { ...prev.materials };
      if (field === 'add') newMaterials[dept] = [...(newMaterials[dept] || []), value];
      else { newMaterials[dept] = [...(newMaterials[dept] || [])]; newMaterials[dept][index] = { ...newMaterials[dept][index], [field]: value }; }
      return { ...prev, materials: newMaterials };
    });
  };
  const deleteMaterial = (dept, index) => setBlueprintData(prev => ({ ...prev, materials: { ...prev.materials, [dept]: prev.materials[dept].filter((_, i) => i !== index) } }));
  const addKPI = () => setBlueprintData(prev => ({ ...prev, objective: { ...prev.objective, kpis: [...prev.objective.kpis, { metric: '', target: '' }] } }));
  const deleteKPI = (idx) => setBlueprintData(prev => ({ ...prev, objective: { ...prev.objective, kpis: prev.objective.kpis.filter((_, i) => i !== idx) } }));
  const addTeamMember = () => setBlueprintData(prev => ({ ...prev, team: { ...prev.team, members: [...prev.team.members, { role: '', name: '' }] } }));
  const deleteTeamMember = (idx) => setBlueprintData(prev => ({ ...prev, team: { ...prev.team, members: prev.team.members.filter((_, i) => i !== idx) } }));
  
  const handleSave = () => {
    const phasesWithDates = blueprintData.phases.timeline.filter(p => p.startDate);
    const startDate = phasesWithDates.length > 0 ? phasesWithDates.reduce((min, p) => p.startDate < min ? p.startDate : min, phasesWithDates[0].startDate) : new Date().toISOString().split('T')[0];
    const phasesWithEndDates = blueprintData.phases.timeline.filter(p => p.endDate);
    const endDate = phasesWithEndDates.length > 0 ? phasesWithEndDates.reduce((max, p) => p.endDate > max ? p.endDate : max, phasesWithEndDates[0].endDate) : startDate;
    const currentPhase = getCurrentPhase(blueprintData.phases.timeline);
    
    // Default preparation checklist for new campaigns
    const defaultPreparation = { funnel: false, budget: false, goals: false, package: false, script: false, landing: false, ads: false, testimoni: false };
    
    const campaignData = {
      id: campaign?.id || Date.now(), name: blueprintData.basic.name, type: blueprintData.basic.type, description: blueprintData.basic.description,
      startDate, endDate, status: mode === 'create' ? (currentPhase?.id || 'planning') : campaignStatusRef.current, blueprint: blueprintData,
      metrics: blueprintData.objective.kpis.filter(k => k.target).map(k => ({ label: k.metric, value: '0', target: k.target })),
      preparation: campaign?.preparation || defaultPreparation,
      subCampaigns: campaign?.subCampaigns || [],
      channel: campaign?.channel || 'public',
    };
    onSave(campaignData);
    onOpenChange(false);
  };
  
  const [campaignStatus, setCampaignStatus] = useState(campaign?.status || 'planning');
  const campaignStatusRef = useRef(campaignStatus);
  useEffect(() => { campaignStatusRef.current = campaignStatus; }, [campaignStatus]);
  
  // AI Upload Deck extraction
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  const handleUploadDeck = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/campaign/extract-from-file`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.detail || 'Extraction failed');
      
      // Merge extracted data with current blueprint
      const ext = data.extracted;
      setBlueprintData(prev => {
        const merged = { ...prev };
        // Basic
        if (ext.basic) {
          if (ext.basic.name) merged.basic = { ...merged.basic, name: ext.basic.name };
          if (ext.basic.type) merged.basic = { ...merged.basic, type: ext.basic.type };
          if (ext.basic.description) merged.basic = { ...merged.basic, description: ext.basic.description };
          if (ext.basic.usp?.length) merged.basic = { ...merged.basic, usp: ext.basic.usp.filter(u => u.point) };
        }
        // Objective
        if (ext.objective) {
          if (ext.objective.mainGoal) merged.objective = { ...merged.objective, mainGoal: ext.objective.mainGoal };
          if (ext.objective.kpis?.length) merged.objective = { ...merged.objective, kpis: ext.objective.kpis.filter(k => k.metric) };
        }
        // Team
        if (ext.team?.members?.length) merged.team = { ...merged.team, members: ext.team.members.filter(m => m.role || m.name) };
        // Offer
        if (ext.offer?.packages?.length) merged.offer = { ...merged.offer, packages: ext.offer.packages };
        // Ad Strategy
        if (ext.adstrategy) {
          if (ext.adstrategy.objective) merged.adstrategy = { ...merged.adstrategy, objective: ext.adstrategy.objective };
          if (ext.adstrategy.totalBudget) merged.adstrategy = { ...merged.adstrategy, totalBudget: ext.adstrategy.totalBudget };
          if (ext.adstrategy.adSets?.length) merged.adstrategy = { ...merged.adstrategy, adSets: ext.adstrategy.adSets };
        }
        // Phases
        if (ext.phases?.timeline?.length) {
          merged.phases = { ...merged.phases, timeline: ext.phases.timeline.map((p, i) => ({ ...merged.phases.timeline[i], ...p, id: merged.phases.timeline[i]?.id || `phase-${i}` })) };
        }
        // Budget
        if (ext.budget) {
          if (ext.budget.totalBudget) merged.budget = { ...merged.budget, totalBudget: ext.budget.totalBudget };
          if (ext.budget.campaignDays) merged.budget = { ...merged.budget, campaignDays: ext.budget.campaignDays };
        }
        return merged;
      });
      
      // Show success with notes
      const notes = [ext.marketing_notes, ext.sales_notes, ext.additional_notes].filter(Boolean).join('\n');
      alert(`✅ Data extracted from "${file.name}"!\n\nReview each step and edit as needed.\n${notes ? '\nAI Notes:\n' + notes : ''}`);
    } catch (err) {
      alert('❌ ' + (err.message || 'Failed to extract data from file'));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };
  
  const currentStepConfig = blueprintSteps[currentStep - 1];
  const hasName = !!blueprintData.basic.name;
  const hasPhaseDate = blueprintData.phases.timeline.some(p => p.startDate);
  const canSave = hasName && hasPhaseDate;
  
  // Generate save button tooltip message
  const getSaveTooltip = () => {
    if (canSave) return '';
    const missing = [];
    if (!hasName) missing.push('Campaign Name');
    if (!hasPhaseDate) missing.push('At least 1 Phase Date');
    return `Sila isi: ${missing.join(', ')}`;
  };
  
  const statusOptions = [
    { id: 'planning', label: 'Planning', color: '#6B7280' }, { id: 'preparation', label: 'Preparation', color: '#F59E0B' },
    { id: 'prospecting', label: 'Prospecting', color: '#8B5CF6' }, { id: 'seeding', label: 'Seeding', color: '#06B6D4' },
    { id: 'buzz', label: 'Buzz', color: '#EC4899' }, { id: 'launching', label: 'Launching', color: '#F97316' },
    { id: 'running', label: 'Running', color: '#10B981' }, { id: 'closing', label: 'Closing', color: '#14B8A6' },
    { id: 'completed', label: 'Completed', color: '#3B82F6' },
  ];
  const currentStatusConfig = statusOptions.find(s => s.id === campaignStatus) || statusOptions[0];
  
  // Show Save button in edit mode (not create mode) - only alongside Next, not at last step
  const showSaveButton = mode !== 'create' && currentStep < blueprintSteps.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] max-w-5xl max-h-[90vh] overflow-hidden flex flex-col p-0 focus:outline-none focus:ring-0 [&>*]:focus:outline-none"
        onKeyDown={(e) => { /* Prevent bubbling to avoid focus ring */ }}>
        <VisuallyHidden.Root><DialogTitle>Campaign Blueprint</DialogTitle><DialogDescription>Create or edit campaign blueprint</DialogDescription></VisuallyHidden.Root>
        
        <div className="px-6 py-4 border-b border-[hsl(var(--border))] bg-gradient-to-r from-[#E63946]/10 via-[#F97316]/5 to-transparent">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#E63946] to-[#F97316] flex items-center justify-center shadow-lg"><Layers size={24} className="text-white" /></div>
              <div>
                <h2 className="text-lg font-bold text-white">{mode === 'create' ? 'Create Campaign Blueprint' : 'Campaign Blueprint'}</h2>
                <p className="text-sm text-[#9CA3AF]">{blueprintData.basic.name || 'New Campaign'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {mode !== 'create' && (
                <Select value={campaignStatus} onValueChange={(v) => setCampaignStatus(v)}>
                  <SelectTrigger className="w-40 h-9 border-2 bg-[hsl(var(--surface-elevated))]" style={{ borderColor: currentStatusConfig.color }}>
                    <div className="flex items-center gap-2"><div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: currentStatusConfig.color }} /><span className="text-sm font-medium text-white">{currentStatusConfig.label}</span></div>
                  </SelectTrigger>
                  <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                    {statusOptions.map(status => (<SelectItem key={status.id} value={status.id} className="text-white"><div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full" style={{ backgroundColor: status.color }} />{status.label}</div></SelectItem>))}
                  </SelectContent>
                </Select>
              )}
              <div className="flex items-center bg-[hsl(var(--surface-elevated))] rounded-lg p-1">
                <Button size="sm" variant={viewMode === 'edit' ? 'default' : 'ghost'} className={`h-8 px-3 text-xs ${viewMode === 'edit' ? 'bg-[#E63946]' : 'text-[#9CA3AF]'}`} onClick={() => setViewMode('edit')}><Edit2 size={14} className="mr-1" />Edit</Button>
                <Button size="sm" variant={viewMode === 'document' ? 'default' : 'ghost'} className={`h-8 px-3 text-xs ${viewMode === 'document' ? 'bg-[#E63946]' : 'text-[#9CA3AF]'}`} onClick={() => setViewMode('document')}><FileStack size={14} className="mr-1" />Overview</Button>
              </div>
              {/* Upload Deck Button */}
              <input type="file" ref={fileInputRef} onChange={handleUploadDeck} accept=".pdf,.pptx,.ppt,.docx,.doc,.txt" className="hidden" />
              <Button size="sm" variant="outline" className="h-8 px-3 text-xs border-[#8b5cf6]/40 text-[#a78bfa] hover:bg-[#8b5cf6]/10" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="upload-deck-btn">
                {uploading ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Upload size={13} className="mr-1" />}
                {uploading ? 'Extracting...' : 'Upload Deck'}
              </Button>
            </div>
          </div>
          <PhaseTimelineVisual phases={blueprintData.phases.timeline} currentCampaignStatus={campaignStatus} />
        </div>
        
        <div className="flex-1 overflow-y-auto">
          {viewMode === 'document' ? (<DocumentView data={blueprintData} />) : (
            <div className="p-6">
              <StepIndicator steps={blueprintSteps} currentStep={currentStep} onStepClick={setCurrentStep} />
              <div className="mt-4 min-h-[350px]">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${currentStepConfig.color}20` }}>
                    {React.createElement(currentStepConfig.icon, { size: 20, style: { color: currentStepConfig.color } })}
                  </div>
                  <div><h3 className="text-lg font-bold text-white">{currentStepConfig.label}</h3><p className="text-xs text-[#9CA3AF]">Step {currentStep} of {blueprintSteps.length}</p></div>
                </div>
                
                {currentStep === 1 && (
                  <div className="space-y-4">
                    <div><Label className="text-[#9CA3AF] text-sm">Campaign Name *</Label><Input value={blueprintData.basic.name} onChange={(e) => updateField('basic', 'name', e.target.value)} placeholder="e.g., March Gold Trading Challenge" className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1 text-lg" /></div>
                    <div><Label className="text-[#9CA3AF] text-sm">Campaign Type *</Label>
                      <Select value={blueprintData.basic.type} onValueChange={(v) => updateField('basic', 'type', v)}>
                        <SelectTrigger className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                          {Object.entries(campaignTypes).map(([key, config]) => (<SelectItem key={key} value={key} className="text-white"><div className="flex items-center gap-2">{React.createElement(config.icon, { size: 14, style: { color: config.color } })}{config.label}</div></SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label className="text-[#9CA3AF] text-sm">Description</Label><Textarea value={blueprintData.basic.description} onChange={(e) => updateField('basic', 'description', e.target.value)} placeholder="Brief description..." className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1 min-h-[80px]" /></div>
                    
                    {/* USP Section */}
                    <div className="pt-4 border-t border-[hsl(var(--border))]">
                      <Label className="text-[#9CA3AF] text-sm flex items-center gap-2 mb-3">
                        <Sparkles size={14} className="text-[#F59E0B]" />
                        Unique Selling Points (USP)
                      </Label>
                      <p className="text-xs text-[#6B7280] mb-3">Apa yang membezakan campaign/offer ini dari pesaing?</p>
                      <div className="space-y-2">
                        {(blueprintData.basic.usp || []).map((usp, idx) => (
                          <div key={idx} className="flex items-center gap-2 group">
                            <div className="w-6 h-6 rounded bg-[#F59E0B]/20 flex items-center justify-center text-[#F59E0B] text-xs font-bold">{idx + 1}</div>
                            <Input 
                              value={usp.point || ''} 
                              onChange={(e) => {
                                const newUsp = [...(blueprintData.basic.usp || [])];
                                newUsp[idx] = { ...newUsp[idx], point: e.target.value };
                                updateField('basic', 'usp', newUsp);
                              }}
                              placeholder={`USP Point ${idx + 1}...`}
                              className="flex-1 bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white"
                            />
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="opacity-0 group-hover:opacity-100 h-8 w-8 p-0 text-[#E63946]"
                              onClick={() => {
                                const newUsp = (blueprintData.basic.usp || []).filter((_, i) => i !== idx);
                                updateField('basic', 'usp', newUsp);
                              }}
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        ))}
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="mt-2 border-dashed border-[#F59E0B]/50 text-[#F59E0B] hover:bg-[#F59E0B]/10"
                        onClick={() => {
                          const newUsp = [...(blueprintData.basic.usp || []), { point: '' }];
                          updateField('basic', 'usp', newUsp);
                        }}
                      >
                        <Plus size={14} className="mr-1" />Tambah USP Point
                      </Button>
                    </div>
                  </div>
                )}
                
                {currentStep === 2 && (
                  <div className="space-y-4">
                    <div><Label className="text-[#9CA3AF] text-sm">Main Objective *</Label><Textarea value={blueprintData.objective.mainGoal} onChange={(e) => updateField('objective', 'mainGoal', e.target.value)} placeholder="e.g., Launch VIP program dengan 100 members dalam 30 hari" className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1 min-h-[60px]" /></div>
                    <div><Label className="text-[#9CA3AF] text-sm mb-2 block">KPIs (Master)</Label>
                      <div className="space-y-2">
                        {blueprintData.objective.kpis.map((kpi, idx) => (
                          <div key={idx} className="flex items-center gap-2 p-3 bg-[hsl(var(--surface-elevated))] rounded-lg group">
                            <Select value={kpi.metric} onValueChange={(v) => { const n = [...blueprintData.objective.kpis]; n[idx] = { ...n[idx], metric: v }; updateField('objective', 'kpis', n); }}>
                              <SelectTrigger className="flex-1 bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white"><SelectValue placeholder="Pilih KPI..." /></SelectTrigger>
                              <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                                {defaultKPIs.map(k => (<SelectItem key={k} value={k} className="text-white">{k}</SelectItem>))}
                                <SelectItem value="custom" className="text-[#F59E0B]">+ Custom KPI</SelectItem>
                              </SelectContent>
                            </Select>
                            {kpi.metric === 'custom' && (<Input value={kpi.customMetric || ''} onChange={(e) => { const n = [...blueprintData.objective.kpis]; n[idx] = { ...n[idx], customMetric: e.target.value }; updateField('objective', 'kpis', n); }} placeholder="Nama KPI..." className="w-32 bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white text-sm" />)}
                            <Input value={kpi.target} onChange={(e) => { const n = [...blueprintData.objective.kpis]; n[idx] = { ...n[idx], target: e.target.value }; updateField('objective', 'kpis', n); }} placeholder="Target" className="w-28 bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white text-sm font-bold" />
                            <Button size="sm" variant="ghost" className="opacity-0 group-hover:opacity-100 h-8 w-8 p-0 text-[#E63946]" onClick={() => deleteKPI(idx)}><Trash2 size={16} /></Button>
                          </div>
                        ))}
                      </div>
                      <Button variant="outline" className="w-full mt-2 border-dashed border-[hsl(var(--border))] text-[#9CA3AF]" onClick={addKPI}><Plus size={14} className="mr-1" />Tambah KPI</Button>
                    </div>
                    
                    {/* KPI Breakdown Section - Dynamic based on master KPIs */}
                    {blueprintData.objective.kpis.filter(k => k.metric).length > 0 && (
                      <div className="pt-4 border-t border-[hsl(var(--border))]">
                        <Label className="text-[#9CA3AF] text-sm flex items-center gap-2 mb-3">
                          <TrendingUp size={14} className="text-[#10B981]" />
                          KPI Breakdown (Monthly / Weekly / Daily)
                        </Label>
                        
                        {/* Table Header */}
                        <div className="bg-[hsl(var(--surface-elevated))] rounded-t-xl border border-[hsl(var(--border))] overflow-hidden">
                          <div className="grid grid-cols-4 gap-0">
                            <div className="p-3 border-r border-[hsl(var(--border))] bg-[hsl(var(--surface))]">
                              <p className="text-xs font-bold text-[#9CA3AF]">KPI</p>
                            </div>
                            <div className="p-3 border-r border-[hsl(var(--border))] bg-[#F97316]/10">
                              <p className="text-xs font-bold text-[#F97316]">Monthly</p>
                            </div>
                            <div className="p-3 border-r border-[hsl(var(--border))] bg-[#3B82F6]/10">
                              <p className="text-xs font-bold text-[#3B82F6]">Weekly</p>
                            </div>
                            <div className="p-3 bg-[#10B981]/10">
                              <p className="text-xs font-bold text-[#10B981]">Daily</p>
                            </div>
                          </div>
                        </div>
                        
                        {/* Table Rows - Dynamic based on KPIs */}
                        <div className="border-x border-b border-[hsl(var(--border))] rounded-b-xl overflow-hidden">
                          {blueprintData.objective.kpis.filter(k => k.metric).map((kpi, idx) => {
                            const kpiName = kpi.metric === 'custom' ? kpi.customMetric : kpi.metric;
                            const kpiKey = kpiName?.toLowerCase().replace(/\s+/g, '_') || `kpi_${idx}`;
                            return (
                              <div key={idx} className={`grid grid-cols-4 gap-0 ${idx !== blueprintData.objective.kpis.filter(k => k.metric).length - 1 ? 'border-b border-[hsl(var(--border))]' : ''}`}>
                                <div className="p-3 border-r border-[hsl(var(--border))] bg-[hsl(var(--surface))] flex items-center">
                                  <p className="text-sm text-white font-medium">{kpiName || 'Unnamed KPI'}</p>
                                  {kpi.target && <Badge className="ml-2 text-[9px] bg-[#F59E0B]/20 text-[#F59E0B]">Target: {kpi.target}</Badge>}
                                </div>
                                <div className="p-2 border-r border-[hsl(var(--border))]">
                                  <Input 
                                    type="number"
                                    value={blueprintData.objective.breakdown?.monthly?.[kpiKey] || ''}
                                    onChange={(e) => {
                                      const newBreakdown = { ...blueprintData.objective.breakdown };
                                      newBreakdown.monthly = { ...newBreakdown.monthly, [kpiKey]: e.target.value };
                                      updateField('objective', 'breakdown', newBreakdown);
                                    }}
                                    placeholder="0"
                                    className="h-8 bg-[hsl(var(--surface))] border-[#F97316]/30 text-white text-sm text-center font-mono"
                                  />
                                </div>
                                <div className="p-2 border-r border-[hsl(var(--border))]">
                                  <Input 
                                    type="number"
                                    value={blueprintData.objective.breakdown?.weekly?.[kpiKey] || ''}
                                    onChange={(e) => {
                                      const newBreakdown = { ...blueprintData.objective.breakdown };
                                      newBreakdown.weekly = { ...newBreakdown.weekly, [kpiKey]: e.target.value };
                                      updateField('objective', 'breakdown', newBreakdown);
                                    }}
                                    placeholder="0"
                                    className="h-8 bg-[hsl(var(--surface))] border-[#3B82F6]/30 text-white text-sm text-center font-mono"
                                  />
                                </div>
                                <div className="p-2">
                                  <Input 
                                    type="number"
                                    value={blueprintData.objective.breakdown?.daily?.[kpiKey] || ''}
                                    onChange={(e) => {
                                      const newBreakdown = { ...blueprintData.objective.breakdown };
                                      newBreakdown.daily = { ...newBreakdown.daily, [kpiKey]: e.target.value };
                                      updateField('objective', 'breakdown', newBreakdown);
                                    }}
                                    placeholder="0"
                                    className="h-8 bg-[hsl(var(--surface))] border-[#10B981]/30 text-white text-sm text-center font-mono"
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {currentStep === 3 && (
                  <div className="space-y-3">
                    <p className="text-sm text-[#9CA3AF] mb-2">Assign team members untuk setiap role dalam campaign</p>
                    {blueprintData.team.members.map((member, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-3 bg-[hsl(var(--surface-elevated))] rounded-lg group">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#3B82F6] to-[#8B5CF6] flex items-center justify-center text-white text-sm font-bold">{member.name ? member.name.charAt(0).toUpperCase() : '?'}</div>
                        <Input value={member.role} onChange={(e) => { const n = [...blueprintData.team.members]; n[idx] = { ...n[idx], role: e.target.value }; updateField('team', 'members', n); }} placeholder="Role" className="w-48 bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white" />
                        <Input value={member.name} onChange={(e) => { const n = [...blueprintData.team.members]; n[idx] = { ...n[idx], name: e.target.value }; updateField('team', 'members', n); }} placeholder="Nama" className="flex-1 bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white" />
                        <Button size="sm" variant="ghost" className="opacity-0 group-hover:opacity-100 h-8 w-8 p-0 text-[#E63946]" onClick={() => deleteTeamMember(idx)}><Trash2 size={16} /></Button>
                      </div>
                    ))}
                    <Button variant="outline" className="w-full border-dashed border-[hsl(var(--border))] text-[#9CA3AF]" onClick={addTeamMember}><Plus size={14} className="mr-1" />Tambah Role</Button>
                  </div>
                )}
                
                {currentStep === 4 && <PackageBuilder packages={blueprintData.offer.packages} currency={blueprintData.offer.currency || 'RM'} onUpdate={(packages) => updateField('offer', 'packages', packages)} onCurrencyChange={(currency) => updateField('offer', 'currency', currency)} />}
                {currentStep === 5 && <MaterialsChecklist materials={blueprintData.materials} onUpdate={updateMaterial} onDelete={deleteMaterial} />}
                {currentStep === 6 && <SalesPageStructure salespage={blueprintData.salespage} onUpdate={(salespage) => setBlueprintData(prev => ({ ...prev, salespage }))} />}
                {currentStep === 7 && <MarketingStrategy marketing={blueprintData.marketing} onUpdate={(marketing) => setBlueprintData(prev => ({ ...prev, marketing }))} />}
                {currentStep === 8 && <AdStrategyBuilder adstrategy={blueprintData.adstrategy} onUpdate={(adstrategy) => setBlueprintData(prev => ({ ...prev, adstrategy }))} />}
                {currentStep === 9 && <WorkflowBuilder workflow={blueprintData.workflow} onUpdate={(workflow) => setBlueprintData(prev => ({ ...prev, workflow }))} />}
                
                {currentStep === 10 && (
                  <div className="space-y-3">
                    <div className="p-3 bg-[hsl(var(--surface-elevated))] rounded-lg border border-[hsl(var(--border))]">
                      <div className="flex flex-wrap gap-4 text-xs"><span className="text-[#6B7280]">⏳ Pending</span><span className="text-[#F59E0B]">▶️ Running</span><span className="text-[#10B981]">✅ Completed</span><span className="text-[#EF4444]">⊘ Skipped</span></div>
                    </div>
                    {blueprintData.phases.timeline.map((phase, idx) => {
                      const config = campaignPhases.find(p => p.id === phase.id);
                      const isSkipped = phase.status === 'skipped';
                      return (
                        <div key={phase.id} className={`p-3 rounded-lg border-l-4 ${isSkipped ? 'opacity-50 bg-[#1a1a1a]' : 'bg-[hsl(var(--surface-elevated))]'}`} style={{ borderColor: isSkipped ? '#6B7280' : config?.color }}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">{React.createElement(config?.icon || Circle, { size: 16, style: { color: isSkipped ? '#6B7280' : config?.color } })}<span className={`text-sm font-semibold ${isSkipped ? 'text-[#6B7280] line-through' : 'text-white'}`}>{phase.label}</span></div>
                            <Select value={phase.status} onValueChange={(v) => { const n = [...blueprintData.phases.timeline]; n[idx] = { ...n[idx], status: v }; updateField('phases', 'timeline', n); }}>
                              <SelectTrigger className="w-32 h-8 bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                                <SelectItem value="pending" className="text-white text-xs">⏳ Pending</SelectItem>
                                <SelectItem value="running" className="text-white text-xs">▶️ Running</SelectItem>
                                <SelectItem value="completed" className="text-white text-xs">✅ Completed</SelectItem>
                                <SelectItem value="skipped" className="text-[#EF4444] text-xs">⊘ Skipped</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          {!isSkipped && (
                            <div className="grid grid-cols-2 gap-2">
                              <div><Label className="text-[9px] text-[#6B7280]">Start Date</Label><Input type="date" value={phase.startDate} onChange={(e) => { const n = [...blueprintData.phases.timeline]; n[idx] = { ...n[idx], startDate: e.target.value }; updateField('phases', 'timeline', n); }} className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white text-xs [color-scheme:dark]" /></div>
                              <div><Label className="text-[9px] text-[#6B7280]">End Date</Label><Input type="date" value={phase.endDate} onChange={(e) => { const n = [...blueprintData.phases.timeline]; n[idx] = { ...n[idx], endDate: e.target.value }; updateField('phases', 'timeline', n); }} className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white text-xs [color-scheme:dark]" /></div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {currentStep === 11 && <BudgetByPhaseAndPlatform budget={blueprintData.budget} onUpdate={(budget) => setBlueprintData(prev => ({ ...prev, budget }))} phasesTimeline={blueprintData.phases?.timeline} />}
              </div>
            </div>
          )}
        </div>
        
        <div className="px-6 py-3 border-t border-[hsl(var(--border))] bg-[hsl(var(--surface-elevated))]">
          <div className="flex items-center justify-between">
            <Button variant="outline" onClick={() => alert('Export PDF')} className="border-[hsl(var(--border))] text-white"><Download size={14} className="mr-1" />PDF</Button>
            <div className="flex items-center gap-2">
              {viewMode === 'edit' && currentStep > 1 && (
                <Button variant="outline" onClick={() => setCurrentStep(s => s - 1)} className="border-[hsl(var(--border))] text-white"><ChevronLeft size={14} className="mr-1" />Prev</Button>
              )}
              
              {/* SAVE button for edit mode - shows alongside Next (not at last step) */}
              {showSaveButton && viewMode === 'edit' && (
                <div className="relative group">
                  <Button onClick={handleSave} disabled={!canSave} className="bg-gradient-to-r from-[#10B981] to-[#06B6D4] text-white disabled:opacity-50 disabled:cursor-not-allowed">
                    <Save size={14} className="mr-1" />Save
                  </Button>
                  {!canSave && (
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-[#1F2937] text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50">
                      {getSaveTooltip()}
                      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[#1F2937]" />
                    </div>
                  )}
                </div>
              )}
              
              {viewMode === 'edit' && currentStep < blueprintSteps.length ? (
                <Button onClick={() => setCurrentStep(s => s + 1)} className="bg-gradient-to-r from-[#E63946] to-[#F97316] text-white">Next<ChevronRight size={14} className="ml-1" /></Button>
              ) : viewMode === 'edit' ? (
                <div className="relative group">
                  <Button onClick={handleSave} disabled={!canSave} className="bg-gradient-to-r from-[#10B981] to-[#06B6D4] text-white disabled:opacity-50 disabled:cursor-not-allowed">
                    <Save size={14} className="mr-1" />{mode === 'create' ? 'Create' : 'Save'}
                  </Button>
                  {!canSave && (
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-[#1F2937] text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50">
                      {getSaveTooltip()}
                      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[#1F2937]" />
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default CampaignBlueprintWizard;
